import { API_BASE_URL, API_HEALTHCHECK_URL } from "./runtimeConfig";
import { getPermissionDeniedMessage } from "./permissions";
import { toast } from "sonner";
import type { TenantCapabilities } from "./capabilities";
import type { OfflineSyncRequest, OfflineSyncResponse } from "./offline/types";
import { REFRESH_TIMEOUT_MS, isRefreshTransientFailure, requestTokenRefresh } from "./authRefresh";
import {
  addDaysToDateKey,
  businessDateKey,
  isDateKey,
  resolveBusinessTimeZone,
  startOfBusinessWeekKey,
} from "./businessTime";
import type {
  EducationEventInput,
  ProductEducationMeDto,
  ReleaseHistoryItemDto,
  AdminRelease,
  AdminEducationItem,
  ValidationResult,
  PreviewResult,
  ReleaseAnalytics,
  EducationAnchorInfo,
} from "@/types/productEducation";

// API Base Configuration
const DASHBOARD_LITE_ENDPOINTS_ENABLED =
  import.meta.env.VITE_DASHBOARD_LITE_ENDPOINTS === "true";

// --- STORAGE KEYS ---
const KEY_TOKEN = "token";
const KEY_REFRESH = "refreshToken";
const KEY_TENANT = "tenantId";
const KEY_USER = "user"; // For basic user info in session
const KEY_ORIGINAL_TOKEN = "originalToken"; // For impersonation
const KEY_ORIGINAL_REFRESH = "originalRefreshToken"; // For impersonation
const AUTH_CHANGE_EVENT = "echo-crm:auth-change";

interface JwtPayload {
  sub?: string;
  exp?: number;
  tenantId?: string;
  isSuperAdmin?: boolean;
  [key: string]: unknown;
}

interface AuthSnapshot {
  token: string | null;
  tenantId: string | null;
  refreshToken: string | null;
}

type ApiResponseType = "json" | "text" | "blob" | "arrayBuffer" | "raw";

interface ApiRequestOptions extends Omit<RequestInit, "headers" | "body"> {
  headers?: HeadersInit;
  body?: unknown;
  responseType?: ApiResponseType;
  skipRefresh?: boolean;
  timeoutMs?: number;
}

type ApiError = Error & {
  status: number;
  response: {
    status: number;
    data: unknown;
  };
};

// --- STATE ---
// We initialize state by pulling from sessionStorage
let authToken: string | null = sessionStorage.getItem(KEY_TOKEN);
let refreshToken: string | null = sessionStorage.getItem(KEY_REFRESH);
let originalAuthToken: string | null =
  sessionStorage.getItem(KEY_ORIGINAL_TOKEN);
let originalRefreshToken: string | null =
  sessionStorage.getItem(KEY_ORIGINAL_REFRESH);
let tenantId: string | null = sessionStorage.getItem(KEY_TENANT);
let refreshPromise: Promise<string | null> | null = null;
let redirectingToLogin = false;
const authListeners = new Set<() => void>();

// --- STORAGE HARDENING (Memory + BeforeUnload Sync + Cross-Tab Auth Channel) ---
// To reduce token exposure to XSS, we remove sensitive tokens from sessionStorage
// immediately after reading them, keeping them strictly in memory during the session.
// They are written back to sessionStorage on visibilitychange/pagehide to preserve
// the user session and impersonation state across tab reloads.
// A BroadcastChannel is used to securely pass tokens to newly opened or duplicated tabs.
const clearSensitiveTokensFromStorage = () => {
  sessionStorage.removeItem(KEY_TOKEN);
  sessionStorage.removeItem(KEY_REFRESH);
  sessionStorage.removeItem(KEY_ORIGINAL_TOKEN);
  sessionStorage.removeItem(KEY_ORIGINAL_REFRESH);
};

// Initial cleanup
clearSensitiveTokensFromStorage();

const persistTokens = () => {
  if (authToken) sessionStorage.setItem(KEY_TOKEN, authToken);
  if (refreshToken) sessionStorage.setItem(KEY_REFRESH, refreshToken);
  if (originalAuthToken)
    sessionStorage.setItem(KEY_ORIGINAL_TOKEN, originalAuthToken);
  if (originalRefreshToken)
    sessionStorage.setItem(KEY_ORIGINAL_REFRESH, originalRefreshToken);
};

window.addEventListener("pagehide", persistTokens);
window.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") {
    persistTokens();
  } else if (document.visibilityState === "visible") {
    clearSensitiveTokensFromStorage();
  }
});

const authChannel = new BroadcastChannel("echo-crm-auth-sync");
authChannel.onmessage = (event) => {
  if (event.data.type === "REQUEST_TOKENS" && authToken) {
    const userStr = sessionStorage.getItem(KEY_USER);
    const userObj = userStr ? JSON.parse(userStr) : null;
    authChannel.postMessage({
      type: "PROVIDE_TOKENS",
      tokens: {
        authToken,
        refreshToken,
        originalAuthToken,
        originalRefreshToken,
        tenantId,
        user: userObj,
      },
    });
  } else if (
    event.data.type === "PROVIDE_TOKENS" &&
    !authToken &&
    event.data.tokens
  ) {
    const { tokens } = event.data;
    if (tokens.authToken) {
      authToken = tokens.authToken;
      refreshToken = tokens.refreshToken || null;
      originalAuthToken = tokens.originalAuthToken || null;
      originalRefreshToken = tokens.originalRefreshToken || null;
      if (tokens.tenantId) {
        tenantId = tokens.tenantId;
        sessionStorage.setItem(KEY_TENANT, tokens.tenantId);
      }
      if (tokens.user) {
        sessionStorage.setItem(KEY_USER, JSON.stringify(tokens.user));
      }
      notifyAuthChange();
    }
  }
};

// If this tab opened without tokens, ask existing tabs for them
if (!authToken && !refreshToken) {
  authChannel.postMessage({ type: "REQUEST_TOKENS" });
}

// --- STORAGE HELPERS ---
const storage = {
  getToken: () => authToken,
  setToken: (token: string) => {
    authToken = token;
  },
  clearToken: () => {
    authToken = null;
  },
  getRefreshToken: () => refreshToken,
  setRefreshToken: (token: string) => {
    refreshToken = token;
  },
  clearRefreshToken: () => {
    refreshToken = null;
  },
  // Tenant ID and User are less sensitive and can safely remain in sessionStorage
  getTenantId: () => tenantId,
  setTenantId: (id: string) => {
    tenantId = id;
    sessionStorage.setItem(KEY_TENANT, id);
  },
  clearTenantId: () => {
    tenantId = null;
    sessionStorage.removeItem(KEY_TENANT);
  },
  getUser: (): User | null => {
    try {
      return JSON.parse(sessionStorage.getItem(KEY_USER) || "null");
    } catch {
      return null;
    }
  },
  setUser: (user: unknown) =>
    sessionStorage.setItem(KEY_USER, JSON.stringify(user)),
  clearUser: () => sessionStorage.removeItem(KEY_USER),
  getOriginalToken: () => originalAuthToken,
  setOriginalToken: (token: string) => {
    originalAuthToken = token;
  },
  clearOriginalToken: () => {
    originalAuthToken = null;
  },
  getOriginalRefreshToken: () => originalRefreshToken,
  setOriginalRefreshToken: (token: string) => {
    originalRefreshToken = token;
  },
  clearOriginalRefreshToken: () => {
    originalRefreshToken = null;
  },
  clear: () => {
    authToken = null;
    refreshToken = null;
    originalAuthToken = null;
    originalRefreshToken = null;
    tenantId = null;
    sessionStorage.removeItem(KEY_TENANT);
    sessionStorage.removeItem(KEY_USER);
    // Clean up any stray tokens just in case
    sessionStorage.removeItem(KEY_TOKEN);
    sessionStorage.removeItem(KEY_REFRESH);
    sessionStorage.removeItem(KEY_ORIGINAL_TOKEN);
    sessionStorage.removeItem(KEY_ORIGINAL_REFRESH);
  },
};

function decodeBase64Url(value: string): string {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  return atob(padded);
}

export const decodeJwtPayload = (token: string | null): JwtPayload | null => {
  if (!token) return null;

  const parts = token.split(".");
  if (parts.length < 2) return null;

  try {
    return JSON.parse(decodeBase64Url(parts[1])) as JwtPayload;
  } catch {
    return null;
  }
};

/** Returns true if the token is missing or expires within the given buffer (seconds). */
const isTokenExpiringSoon = (token: string | null, bufferSeconds = 90): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;
  return Date.now() / 1000 >= payload.exp - bufferSeconds;
};

const extractTenantIdFromToken = (token: string | null): string | null => {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  return typeof payload.tenantId === "string" && payload.tenantId.length > 0
    ? payload.tenantId
    : null;
};

const notifyAuthChange = () => {
  authListeners.forEach((listener) => listener());
  window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
};

const syncAuthStateFromStorage = (emit = true): AuthSnapshot => {
  authToken = storage.getToken();
  tenantId = storage.getTenantId();

  if (authToken && !tenantId) {
    const tokenTenantId = extractTenantIdFromToken(authToken);
    if (tokenTenantId) {
      tenantId = tokenTenantId;
      storage.setTenantId(tokenTenantId);
    }
  }

  const snapshot: AuthSnapshot = {
    token: authToken,
    tenantId,
    refreshToken: storage.getRefreshToken(),
  };

  if (emit) notifyAuthChange();
  return snapshot;
};

if (authToken && !tenantId) {
  syncAuthStateFromStorage(false);
}

// In Memory + BeforeUnload strategy, changes to tokens are strictly localized to memory during the session.
// The 'storage' event listener for Token/Refresh is generally obsolete here since they aren't written to storage
// except on beforeunload, meaning cross-tab sharing naturally doesn't work seamlessly via sessionStorage.
// But we keep listening for Tenant if needed.
window.addEventListener("storage", (event) => {
  if (!event.key) return;
  if (event.key === KEY_TENANT) {
    syncAuthStateFromStorage();
  }
});

const isAuthEndpoint = (endpoint: string) =>
  endpoint.includes("/auth/login") ||
  endpoint.includes("/auth/refresh") ||
  endpoint.includes("/auth/logout") ||
  endpoint.includes("/auth/activate-account");

const redirectToLogin = () => {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  if (window.location.pathname !== "/login") {
    window.location.assign("/login");
  } else {
    redirectingToLogin = false;
  }
};

const handleAuthFailure = () => {
  api.clearAuth();
  redirectToLogin();
};

const isBodyInitValue = (body: unknown): body is BodyInit => {
  if (body == null) return false;
  return (
    typeof body === "string" ||
    body instanceof Blob ||
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof ArrayBuffer ||
    ArrayBuffer.isView(body)
  );
};

const attachAuthHeaders = (headers: Headers, endpoint: string) => {
  if (endpoint.includes("/auth/login")) {
    return;
  }

  const token = authToken ?? storage.getToken();
  const currentTenantId = tenantId ?? storage.getTenantId();

  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (currentTenantId && !headers.has("x-tenant-id")) {
    headers.set("x-tenant-id", currentTenantId);
  }
};

const prepareBody = (
  body: unknown,
  headers: Headers,
): BodyInit | null | undefined => {
  if (body === undefined) return undefined;
  if (body === null) return null;
  if (isBodyInitValue(body)) return body;

  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return JSON.stringify(body);
};

const GENERIC_HTTP_MESSAGES = new Set([
  "conflict", "not found", "forbidden", "unauthorized", "bad request",
  "internal server error", "service unavailable", "unprocessable entity",
  "too many requests", "method not allowed", "unsupported media type",
  "precondition failed", "not implemented", "gateway timeout",
]);

const HTTP_STATUS_MESSAGES: Record<number, string> = {
  400: "Los datos enviados no son válidos. Revisa los campos e intenta de nuevo.",
  401: "Sesión expirada o no autenticado. Inicia sesión nuevamente.",
  403: "No tienes permiso para realizar esta acción.",
  404: "El recurso solicitado no existe.",
  409: "Ya existe un registro con esos datos (conflicto de duplicado).",
  413: "El archivo o datos enviados son demasiado grandes.",
  422: "Los datos no pudieron ser procesados. Verifica la información ingresada.",
  429: "Demasiadas solicitudes. Espera un momento e intenta de nuevo.",
  500: "Error interno del servidor. Intenta de nuevo en unos segundos.",
  502: "El servidor no está disponible temporalmente.",
  503: "Servicio no disponible. Intenta de nuevo en unos momentos.",
  504: "El servidor tardó demasiado en responder. Intenta de nuevo.",
};

const parseErrorMessage = (status: number, data: unknown): string => {
  let rawMessage: string | undefined;

  if (typeof data === "string" && data.length > 0) {
    rawMessage = data;
  } else if (typeof data === "object" && data !== null) {
    const d = data as { message?: unknown };
    if (Array.isArray(d.message) && d.message.length > 0) {
      rawMessage = (d.message as unknown[])
        .map((m) => (typeof m === "string" ? m : JSON.stringify(m)))
        .join(", ");
    } else if (typeof d.message === "string" && d.message.length > 0) {
      rawMessage = d.message;
    }
  }

  if (typeof rawMessage === "string") {
    const deniedPermissionMatch = rawMessage.match(/'([A-Z0-9_]+)' permission/i);
    if (deniedPermissionMatch?.[1]) {
      return getPermissionDeniedMessage(deniedPermissionMatch[1]);
    }
  }
  // Si el backend manda un mensaje personalizado (no el boilerplate genérico de HTTP), usarlo tal cual
  if (rawMessage && !GENERIC_HTTP_MESSAGES.has(rawMessage.trim().toLowerCase())) {
    return rawMessage;
  }

  // Caer en mensajes amigables por código HTTP
  if (status in HTTP_STATUS_MESSAGES) return HTTP_STATUS_MESSAGES[status];

  if (status >= 500) {
    return import.meta.env.DEV
      ? `Error del servidor (${status}). Verifica ${API_HEALTHCHECK_URL}.`
      : "Error interno del servidor. Intenta de nuevo en unos segundos.";
  }

  return `Error ${status}. Intenta de nuevo.`;
};

const createApiError = (status: number, data: unknown): ApiError => {
  const error = new Error(parseErrorMessage(status, data)) as ApiError;
  error.status = status;
  error.response = { status, data };
  return error;
};

const createTimeoutError = (timeoutMs: number): ApiError => {
  const message = `La solicitud tardo demasiado (${Math.ceil(timeoutMs / 1000)}s). Intenta de nuevo.`;
  return createApiError(408, { message });
};

const parseResponseBody = async (
  response: Response,
  responseType: ApiResponseType,
): Promise<unknown> => {
  if (responseType === "raw") return response;
  if (response.status === 204) return undefined;

  if (responseType === "blob") return response.blob();
  if (responseType === "arrayBuffer") return response.arrayBuffer();
  if (responseType === "text") return response.text();

  const text = await response.text();
  if (!text) return undefined;
  return JSON.parse(text) as unknown;
};

const parseErrorBody = async (response: Response): Promise<unknown> => {
  const contentType = response.headers.get("content-type")?.toLowerCase() || "";
  if (contentType.includes("application/json")) {
    try {
      return await response.json();
    } catch {
      return { message: `HTTP ${response.status}` };
    }
  }

  try {
    const text = await response.text();
    return text ? { message: text } : { message: `HTTP ${response.status}` };
  } catch {
    return { message: `HTTP ${response.status}` };
  }
};

const buildTenantOverrideHeaders = (tenantOverrideId?: string) =>
  tenantOverrideId ? { "x-tenant-id": tenantOverrideId } : undefined;

const handleResponse = async <T>(
  response: Response,
  responseType: ApiResponseType,
): Promise<T> => {
  if (!response.ok) {
    const errorData = await parseErrorBody(response);
    throw createApiError(response.status, errorData);
  }
  return parseResponseBody(response, responseType) as Promise<T>;
};

// --- HELPER: Refresh Access Token (Single Flight + Rotation) ---
// Offline Safety: only a real auth rejection (400/401/403 from /auth/refresh,
// or an invalid refresh response) logs out. A NETWORK failure / timeout
// rejects with RefreshNetworkError (a TypeError) and a 408/429/5xx with
// RefreshTransientError — the session is kept, so a cashier working offline
// is never logged out (see lib/authRefresh.ts).
async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  const pending = (async (): Promise<string | null> => {
    const currentRefreshToken = storage.getRefreshToken();
    if (!currentRefreshToken) {
      handleAuthFailure();
      return null;
    }

    if (import.meta.env.DEV) {
      console.info("[API] Refreshing access token");
    }

    const outcome = await requestTokenRefresh({
      url: `${API_BASE_URL}/auth/refresh`,
      refreshToken: currentRefreshToken,
      tenantId,
      timeoutMs: REFRESH_TIMEOUT_MS,
    });

    if (outcome.kind !== "ok" || !outcome.accessToken || !outcome.refreshToken) {
      console.error("[API] Refresh rejected, logging out", outcome.reason);
      handleAuthFailure();
      return null;
    }

    api.setAuth(outcome.accessToken, outcome.refreshToken);

    if (import.meta.env.DEV) {
      console.info("[API] Access token refreshed");
    }
    return outcome.accessToken;
  })();
  refreshPromise = pending;
  // Release the single-flight slot AFTER assigning it, on every outcome. (The
  // old in-body finally ran before the assignment on the synchronous
  // missing-refresh-token path, leaving a resolved null cached forever — later
  // refreshes never ran until a full reload.)
  const release = () => {
    if (refreshPromise === pending) refreshPromise = null;
  };
  pending.then(release, release);

  return pending;
}

const executeRequest = async <T>(
  endpoint: string,
  options: ApiRequestOptions = {},
): Promise<T> => {
  const responseType = options.responseType ?? "json";
  const headers = new Headers(options.headers ?? {});
  const body = prepareBody(options.body, headers);
  const controller = new AbortController();
  const externalSignal = options.signal;
  const timeoutMs = options.timeoutMs;

  let timeoutId: number | null = null;
  let abortHandler: (() => void) | null = null;

  // Proactively refresh the access token if it expires within 90 seconds
  // and we have a refresh token available. This prevents avoidable 401s on
  // mutations (e.g. PATCH /saas/tenants/:id) when the admin has been idle.
  if (
    !isAuthEndpoint(endpoint) &&
    !options.skipRefresh &&
    isTokenExpiringSoon(authToken) &&
    storage.getRefreshToken()
  ) {
    try {
      await refreshAccessToken();
    } catch (error) {
      // Proactive refresh only: on a network/transient failure keep going
      // with the current token (it may still be valid; if the network is
      // down the request itself fails as a normal network error).
      if (!isRefreshTransientFailure(error)) throw error;
    }
  }

  attachAuthHeaders(headers, endpoint);

  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort((externalSignal as AbortSignal).reason);
    } else {
      abortHandler = () =>
        controller.abort((externalSignal as AbortSignal).reason);
      externalSignal.addEventListener("abort", abortHandler, { once: true });
    }
  }

  if (typeof timeoutMs === "number" && timeoutMs > 0) {
    timeoutId = window.setTimeout(() => {
      controller.abort(createTimeoutError(timeoutMs));
    }, timeoutMs);
  }

  const requestInit: RequestInit = {
    ...options,
    headers,
    body,
    signal: controller.signal,
  };

  try {
    let response = await fetch(`${API_BASE_URL}${endpoint}`, requestInit);

    const shouldTryRefresh =
      response.status === 401 &&
      !options.skipRefresh &&
      !isAuthEndpoint(endpoint);

    if (shouldTryRefresh) {
      const newToken = await refreshAccessToken();

      if (newToken) {
        headers.set("Authorization", `Bearer ${newToken}`);
        response = await fetch(`${API_BASE_URL}${endpoint}`, {
          ...requestInit,
          headers,
          body,
          signal: controller.signal,
        });
      }
    }

    const result = await handleResponse<T>(response, responseType);
    // Server responded — dismiss any stale maintenance banner
    toast.dismiss("maintenance");
    return result;
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      if (controller.signal.reason instanceof Error) {
        throw controller.signal.reason;
      }

      if (typeof timeoutMs === "number" && timeoutMs > 0) {
        throw createTimeoutError(timeoutMs);
      }
    }

    if (error instanceof TypeError) {
      // Network error. Distinguish "this device has no connection at all"
      // (routine, especially relevant in modo offline — never frame it as a
      // server problem) from "the network interface is up but the backend
      // itself didn't respond" (genuinely worth flagging as maintenance).
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        toast.error("Sin conexión a internet.", {
          id: "maintenance",
          duration: 4000,
        });
      } else {
        toast.error("Sistema en mantenimiento. Contacta al administrador.", {
          id: "maintenance",
          duration: Infinity,
        });
      }
    }

    throw error;
  } finally {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }
    if (externalSignal && abortHandler) {
      externalSignal.removeEventListener("abort", abortHandler);
    }
  }
};

// --- API CLIENT ---
export const api = {
  // Set full auth context
  setAuth: (token: string, refreshToken: string, tId?: string) => {
    authToken = token;
    storage.setToken(token);

    if (refreshToken) {
      storage.setRefreshToken(refreshToken);
    } else {
      storage.clearRefreshToken();
    }

    const resolvedTenantId = tId ?? extractTenantIdFromToken(token);
    tenantId = resolvedTenantId;
    if (resolvedTenantId) {
      storage.setTenantId(resolvedTenantId);
    } else {
      storage.clearTenantId();
    }

    redirectingToLogin = false;
    notifyAuthChange();
  },

  // Legacy/Simple setter compatibility
  setToken: (token: string) => {
    authToken = token;
    storage.setToken(token);

    const resolvedTenantId = extractTenantIdFromToken(token);
    tenantId = resolvedTenantId;
    if (resolvedTenantId) {
      storage.setTenantId(resolvedTenantId);
    } else {
      storage.clearTenantId();
    }

    notifyAuthChange();
  },

  clearAuth: () => {
    authToken = null;
    tenantId = null;
    refreshPromise = null;
    storage.clear();
    notifyAuthChange();
  },

  // Compatible alias
  clearToken: () => api.clearAuth(),

  getToken: () => authToken ?? storage.getToken(),
  getOriginalToken: () => originalAuthToken ?? storage.getOriginalToken(),
  clearOriginalToken: () => {
    storage.clearOriginalToken();
    storage.clearOriginalRefreshToken();
    sessionStorage.removeItem(KEY_ORIGINAL_TOKEN);
    sessionStorage.removeItem(KEY_ORIGINAL_REFRESH);
    notifyAuthChange();
  },
  getTenantId: () => tenantId ?? storage.getTenantId(),
  getAuthSnapshot: (): AuthSnapshot => ({
    token: authToken ?? storage.getToken(),
    tenantId: tenantId ?? storage.getTenantId(),
    refreshToken: storage.getRefreshToken(),
  }),
  syncAuthFromStorage: () => syncAuthStateFromStorage(),
  subscribeAuth: (listener: () => void) => {
    authListeners.add(listener);
    return () => authListeners.delete(listener);
  },
  getCurrentUser: () => storage.getUser(),
  setCurrentUser: (user: unknown) => storage.setUser(user),

  // Generic Request
  request: <T>(endpoint: string, options: ApiRequestOptions = {}) =>
    executeRequest<T>(endpoint, {
      ...options,
      responseType: options.responseType ?? "json",
    }),

  // FormData Request (No Content-Type header override)
  requestFormData: <T>(
    endpoint: string,
    formData: FormData,
    method = "POST",
    options: Omit<ApiRequestOptions, "method" | "body"> = {},
  ) =>
    executeRequest<T>(endpoint, {
      ...options,
      method,
      body: formData,
      responseType: "json",
    }),

  // Convenience methods
  get: <T>(
    endpoint: string,
    options: Omit<ApiRequestOptions, "method" | "body"> = {},
  ) =>
    executeRequest<T>(endpoint, {
      ...options,
      method: "GET",
      responseType: options.responseType ?? "json",
    }),
  post: <T>(
    endpoint: string,
    data?: unknown,
    options: Omit<ApiRequestOptions, "method" | "body"> = {},
  ) =>
    executeRequest<T>(endpoint, {
      ...options,
      method: "POST",
      body: data,
      responseType: options.responseType ?? "json",
    }),
  put: <T>(
    endpoint: string,
    data?: unknown,
    options: Omit<ApiRequestOptions, "method" | "body"> = {},
  ) =>
    executeRequest<T>(endpoint, {
      ...options,
      method: "PUT",
      body: data,
      responseType: options.responseType ?? "json",
    }),
  patch: <T>(
    endpoint: string,
    data?: unknown,
    options: Omit<ApiRequestOptions, "method" | "body"> = {},
  ) =>
    executeRequest<T>(endpoint, {
      ...options,
      method: "PATCH",
      body: data,
      responseType: options.responseType ?? "json",
    }),
  delete: <T>(
    endpoint: string,
    data?: unknown,
    options: Omit<ApiRequestOptions, "method" | "body"> = {},
  ) =>
    executeRequest<T>(endpoint, {
      ...options,
      method: "DELETE",
      body: data,
      responseType: options.responseType ?? "json",
    }),

  // Binary helpers (PDFs, exports, etc.)
  getBlob: (
    endpoint: string,
    options: Omit<ApiRequestOptions, "method" | "body" | "responseType"> = {},
  ) =>
    executeRequest<Blob>(endpoint, {
      ...options,
      method: "GET",
      responseType: "blob",
    }),
  getArrayBuffer: (
    endpoint: string,
    options: Omit<ApiRequestOptions, "method" | "body" | "responseType"> = {},
  ) =>
    executeRequest<ArrayBuffer>(endpoint, {
      ...options,
      method: "GET",
      responseType: "arrayBuffer",
    }),
};

export interface BackendVersionInfo {
  status: "ok";
  appName: string;
  environment: string;
  version: string;
  gitCommitHash: string | null;
  buildTimestamp: string | null;
  nodeVersion: string;
  uptimeSeconds: number;
  startedAt: string;
}

export const diagnosticsApi = {
  getBackendVersion: () => api.get<BackendVersionInfo>("/health/version"),
};

// Auth API
export type TenantIndustry =
  | "BOUTIQUE"
  | "GROCERY"
  | "RESTAURANT"
  | "BEAUTY"
  | "COSMETICS"
  | "JEWELRY"
  | "PHONES";

export type InventoryBootstrapMode = "EMPTY" | "PRELOADED";
export type UserStatus = "INVITED" | "ACTIVE" | "INACTIVE";

export type TenantStatus =
  | "ACTIVE"
  | "TRIAL"
  | "PAST_DUE"
  | "SUSPENDED"
  | "TERMINATED";

export interface TenantWarehouseSummary {
  id: string;
  name: string;
  type: string;
  storeId: string | null;
  isActive: boolean;
  location?: string | null;
  createdAt?: string;
  updatedAt?: string;
  store?: {
    id: string;
    name: string;
  } | null;
}

export interface LatestInvitationSummary {
  id: string;
  createdAt: string;
  expiresAt: string;
  lastSentAt?: string | null;
  usedAt?: string | null;
  lastDeliveryError?: string | null;
}

export interface TenantUserQuotaSummary {
  maxUsers: number;
  configuredMaxUsers: number;
  usedUsers: number;
  availableUsers: number;
  unlimited: boolean;
  countedStatuses: UserStatus[];
  ownerCountsAgainstQuota: false;
}

export interface TenantStoreSummary {
  id: string;
  name: string;
  address?: string | null;
  phone?: string | null;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  warehouses?: TenantWarehouseSummary[];
}

export interface TenantOwnerSummary {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  username?: string;
  isActive: boolean;
  status: UserStatus;
  mustChangePassword: boolean;
  createdAt: string;
  latestInvitation?: LatestInvitationSummary | null;
}

export interface TenantAdminNoteRecord {
  id: string;
  note: string;
  createdAt: string;
  updatedAt: string;
  author: {
    id: string;
    fullName: string;
    email: string | null;
  } | null;
}

export interface TenantStatusHistoryRecord {
  id: string;
  previousStatus: TenantStatus | null;
  nextStatus: TenantStatus;
  reason: string | null;
  note: string | null;
  changedAt: string;
  changedBy: {
    id: string;
    fullName: string;
    email: string | null;
  } | null;
}

export interface TenantOnboardingChecklistItem {
  key: string;
  label: string;
  done: boolean;
}

export interface TenantOnboardingSnapshot {
  checklist: TenantOnboardingChecklistItem[];
  completionPercent: number;
  readyToOperate: boolean;
  nextStep: string;
  suggestedCategories: string[];
  productCount: number;
  categoryCount: number;
  latestInventoryImport: InventoryImportHistoryEntry | null;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  industry: TenantIndustry;
  status?: TenantStatus;
  plan?: "FREE" | "BASIC" | "PRO" | "ENTERPRISE";
  maxWarehouses?: number;
  maxUsers?: number;
  inventoryBootstrapMode?: InventoryBootstrapMode;
  features?: Record<string, any>;
  /** Effective capabilities resolved by the backend (GET /auth/me). May be
   *  missing on older backends / cached users — use resolveTenantCapabilities. */
  capabilities?: Partial<TenantCapabilities>;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  stores?: TenantStoreSummary[];
  warehouses?: TenantWarehouseSummary[];
  ownerUser?: TenantOwnerSummary | null;
  adminNotes?: TenantAdminNoteRecord[];
  statusHistory?: TenantStatusHistoryRecord[];
  latestStatusChange?: TenantStatusHistoryRecord | null;
  onboarding?: TenantOnboardingSnapshot;
  userQuota?: TenantUserQuotaSummary;
  recentInventoryImports?: InventoryImportHistoryEntry[];
  latestInventoryImport?: InventoryImportHistoryEntry | null;
  categories?: Array<{
    id: string;
    name: string;
    slug: string;
    isActive: boolean;
    sortOrder: number;
  }>;
  // Business info
  phone?: string;
  email?: string;
  primaryContactName?: string | null;
  address?: string;
  currency?: string;
  timezone?: string;
  contractStartAt?: string | null;
  trialEndsAt?: string | null;
  terminatedAt?: string | null;
  documentMode?: "NON_FISCAL" | "FISCAL_REQUIRED" | "HYBRID_CONTROLLED";
  fiscalSuspended?: boolean;
  _count?: {
    users: number;
    sales: number;
    products: number;
    contacts?: number;
    categories?: number;
    stores?: number;
    warehouses?: number;
  };
}

export type Role =
  | "OWNER"
  | "MANAGER"
  | "CASHIER"
  | "ADMIN"
  | "AGENT"
  | "ACCOUNTANT"
  | "WAREHOUSE";

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  username?: string;
  role: Role; // Explicitly typed
  status?: UserStatus;
  isSuperAdmin?: boolean;
  mustChangePassword?: boolean;
  permissions?: string[];
  avatar?: string;
  tenantId: string;
  tenant?: Tenant;
  allowedWarehouses?: Array<{
    id: string;
    name: string;
    storeId?: string;
    storeName?: string;
  }>;
  activeWarehouseId?: string;
  /** Which stores this user may read / operate (from /auth/me). */
  locationScope?: LocationScope | null;
}

/** Location scope resolved by the backend — the authority; the UI only mirrors it. */
export interface LocationScope {
  role: string;
  /** May read every store and the consolidated "Todas las sucursales" view. */
  isGlobal: boolean;
  allowedStoreIds: string[];
  /** CASHIER: the only store they sell from (null = not assigned yet). */
  boundStoreId: string | null;
}

/** A place where stock lives: a store (sucursal) or a warehouse without a store. */
export interface StockLocation {
  key: string;
  storeId: string | null;
  warehouseId: string | null;
  name: string;
  kind: "STORE" | "WAREHOUSE";
}

export interface StockLookupResponse {
  variantId: string;
  productName: string;
  variantLabel: string | null;
  sku: string | null;
  sellUom: string | null;
  serialized: boolean;
  locations: Array<StockLocation & { quantity: number }>;
}

export interface StockByLocationResponse {
  scope: { global: boolean; storeIds: string[] | null };
  locations: StockLocation[];
  rows: Array<{
    variantId: string;
    productName: string;
    variantLabel: string | null;
    sku: string | null;
    sellUom: string | null;
    serialized: boolean;
    total: number;
    byLocation: Record<string, number>;
  }>;
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export interface ScopedDashboardResponse {
  scope: { storeIds: string[] | null; global: boolean };
  rangeDays: number;
  salesTotal: number;
  salesCount: number;
  avgTicket: number;
  salesToday: { total: number; count: number };
  lowStock: {
    count: number;
    items: Array<{
      variantId: string;
      storeId?: string;
      storeName?: string;
      productName: string;
      sku: string | null;
      size: string | null;
      color: string | null;
      sellUom: string | null;
      stock: number;
      minStock: number;
      need?: number | null;
      incoming?: number;
    }>;
  };
  date: string;
}

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

export interface AccountActivationPreview {
  email: string;
  expiresAt: string;
  tenant: {
    id: string;
    businessName: string;
    name: string;
  };
  user: {
    id: string;
    fullName: string;
    status: UserStatus;
  };
}

/**
 * Wraps a promise with a timeout using AbortController.
 * If the promise doesn't resolve within the timeout, it's aborted and an error is thrown.
 */
function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  controller?: AbortController,
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      setTimeout(() => {
        if (controller) {
          controller.abort();
        }
        reject(new Error("TIMEOUT"));
      }, timeoutMs);
    }),
  ]);
}

export const authApi = {
  login: async (email: string, password: string) => {
    const controller = new AbortController();

    try {
      // Add 15 second timeout to login request
      const response = await withTimeout(
        api.post<LoginResponse>(
          "/auth/login",
          {
            email,
            password,
          },
          {
            signal: controller.signal,
          },
        ),
        15000, // 15 second timeout
        controller,
      );

      // Save tokens
      const accessToken = response.accessToken;
      const refreshToken = response.refreshToken;

      if (accessToken && refreshToken) {
        api.setAuth(accessToken, refreshToken);
        if (response.user) {
          api.setCurrentUser(response.user);
        }
        // Un nuevo login reinicia el "ver luego" del popup de novedades: así,
        // si el owner eligió "Ver luego" (o cerró con la X), el popup vuelve a
        // aparecer al iniciar sesión de nuevo. "Entendido" vive en localStorage
        // y no se toca aquí, por eso ese sí lo oculta para siempre.
        try {
          for (let i = sessionStorage.length - 1; i >= 0; i--) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith("maddox.whatsnew.") && key.endsWith(".later")) {
              sessionStorage.removeItem(key);
            }
          }
        } catch {
          /* almacenamiento no disponible — ignorar */
        }
      }

      return {
        accessToken,
        access_token: accessToken,
        refresh_token: refreshToken,
        user: response.user,
      };
    } catch (error: any) {
      // Handle timeout error with Spanish message
      if (error?.message === "TIMEOUT" || error?.name === "AbortError") {
        const timeoutError = new Error(
          "No se pudo conectar con el servidor. Verifica que el servidor esté activo e intenta de nuevo.",
        ) as any;
        timeoutError.isTimeout = true;
        throw timeoutError;
      }
      // Re-throw other errors
      throw error;
    }
  },

  previewAccountActivation: (token: string) =>
    api.post<AccountActivationPreview>(
      "/auth/activate-account/preview",
      { token },
      {
        headers: {
          Authorization: "",
          "x-tenant-id": "",
        },
      },
    ),

  activateAccount: (token: string, password: string) =>
    api.post<{ tenantId: string; userId: string }>(
      "/auth/activate-account",
      { token, password },
      {
        headers: {
          Authorization: "",
          "x-tenant-id": "",
        },
      },
    ),

  me: () => api.get<User>("/auth/me"),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ ok: boolean }>("/auth/change-password", {
      currentPassword,
      newPassword,
    }),

  logout: () =>
    api.post("/auth/logout").finally(() => {
      api.clearAuth();
    }),
};

export interface ProductSettings {
  isConsignment?: boolean;
  consignmentSupplierId?: string;
  supplierRef?: string;
  extras?: Array<{ name: string; price: number }>;
  [key: string]: unknown;
}

export interface ProductVariantRecord {
  id: string;
  sku: string;
  name?: string;
  size?: string;
  color?: string;
  price: number;
  cost: number;
  priceTiers?: Array<{
    id?: string;
    minQty: number;
    unitPrice: number;
    priority?: number;
  }>;
  wholesalePrices?: Array<{
    id: string;
    price: number;
  }>;
  sellUom?: string;
  barcode?: string;
  reference?: string;
  minStock?: number;
  serializedTracking?: boolean;
  stockBalances?: Array<{ quantity: number }>;
  effectiveStock?: number;
  availableStock?: number;
  availableSerializedCount?: number;
  soldSerializedCount?: number;
  reservedSerializedCount?: number;
  serializedSummary?: {
    available: number;
    sold: number;
    reserved: number;
  };
  stockStatus?: "OUT_OF_STOCK" | "LOW_STOCK" | "OK";
  canSell?: boolean;
  isSerialized?: boolean;
}

export interface ProductRecord {
  id: string;
  name: string;
  brand: string;
  /** Always plain text (derived from descriptionHtml when rich). */
  description?: string | null;
  /** Sanitized rich description (null for legacy plain-text products). */
  descriptionHtml?: string | null;
  publishedToCatalog?: boolean;
  archivedAt?: string | null;
  archiveReason?: string | null;
  supplierId?: string;
  categoryId?: string | null;
  category?: {
    id: string;
    name: string;
    slug: string;
    sortOrder: number;
    isActive: boolean;
  } | null;
  imageUrl?: string;
  images?: Array<{ id: string; url: string; order: number }>;
  settings?: ProductSettings;
  variants: ProductVariantRecord[];
}

// Products API
/** An existing catalog item that already owns a SKU / barcode (catalog is tenant-global). */
export interface CatalogIdentityMatch {
  matchedBy: "SKU" | "BARCODE" | "PRESENTATION_BARCODE";
  variantId: string;
  productId: string;
  productName: string;
  variantLabel: string | null;
  sku: string;
  barcode: string | null;
  archived: boolean;
  presentationName?: string;
}

export interface CatalogIdentityCheck {
  sku: CatalogIdentityMatch | null;
  barcode: CatalogIdentityMatch | null;
  similar: Array<{ productId: string; name: string; brand: string | null; variantCount: number; archived: boolean }>;
}

export const productsApi = {
  /**
   * Variant search (name / SKU / barcode / attributes). Receiving and orders
   * call it here; it used to live only on promotionsApi, so
   * productsApi.searchVariants was undefined and the "Recibir mercadería"
   * search silently returned nothing.
   */
  searchVariants: (query: string, limit?: number) =>
    api.get<VariantSearchResult[]>(
      `/products/variants/search?q=${encodeURIComponent(query)}&limit=${limit || 50}`,
    ),

  /** Exact owner of a SKU / barcode + similar product names (suggestion only). */
  identityCheck: (q: { sku?: string; barcode?: string; name?: string; excludeVariantId?: string; excludeProductId?: string }) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v && String(v).trim()) p.append(k, String(v).trim());
    return api.get<CatalogIdentityCheck>(`/products/identity-check?${p}`);
  },

  getAll: async (filters?: {
    q?: string;
    categoryId?: string;
    uncategorized?: boolean;
  }) => {
    const params = new URLSearchParams();
    if (filters?.q) params.set("q", filters.q);
    if (filters?.categoryId) params.set("categoryId", filters.categoryId);
    if (filters?.uncategorized) params.set("uncategorized", "true");
    params.set("limit", "500");
    const suffix = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<{
      data: ProductRecord[];
      meta?: unknown;
    }>(`/products${suffix}`);
    return response.data || [];
  },

  getAllPaginated: async (filters?: {
    q?: string;
    categoryId?: string;
    uncategorized?: boolean;
    includeArchived?: boolean;
    page?: number;
    limit?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.q) params.set("q", filters.q);
    if (filters?.categoryId) params.set("categoryId", filters.categoryId);
    if (filters?.uncategorized) params.set("uncategorized", "true");
    if (filters?.includeArchived) params.set("includeArchived", "true");
    if (filters?.page) params.set("page", String(filters.page));
    if (filters?.limit) params.set("limit", String(filters.limit));
    const suffix = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<{
      data: ProductRecord[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>(`/products${suffix}`);
    return {
      data: response.data || [],
      meta: response.meta ?? { total: 0, page: filters?.page ?? 1, limit: filters?.limit ?? 20, totalPages: 0 },
    };
  },

  getOne: (id: string) => api.get<ProductRecord>(`/products/${id}`),

  create: (data: {
    /** Idempotency key of this create request: a retry/replay returns the same product. */
    idempotencyKey?: string;
    name: string;
    brand?: string;
    description?: string;
    /** Rich description; the server sanitizes it and derives description. */
    descriptionHtml?: string | null;
    publishedToCatalog?: boolean;
    supplierId?: string;
    categoryId?: string | null;
    imageUrl?: string;
    imageUrls?: string[];
    settings?: ProductSettings;
    variants: Array<{
      sku: string;
      name?: string;
      size?: string;
      color?: string;
      cost: number;
      price: number;
      wholesalePrice?: number;
      cashTransferPrice?: number;
      cardPaymentPrice?: number;
      extraFinancingPrice?: number;
      serializedTracking?: boolean;
      sellUom?: string;
      barcode?: string;
      reference?: string;
      priceTiers?: Array<{
        minQty: number;
        unitPrice: number;
        priority?: number;
      }>;
    }>;
  }) => api.post<ProductRecord & { idempotentReplay?: boolean }>("/products", data),

  update: (
    id: string,
    data: {
      name?: string;
      /** null / "" clears */
      brand?: string | null;
      description?: string | null;
      descriptionHtml?: string | null;
      publishedToCatalog?: boolean;
      supplierId?: string | null;
      categoryId?: string | null;
      imageUrl?: string | null;
      /** Authoritative gallery: [0] becomes the primary image; [] clears. */
      imageUrls?: string[];
      /** Only isConsignment/consignmentSupplierId are applied (merged). */
      settings?: Pick<ProductSettings, "isConsignment" | "consignmentSupplierId">;
    },
  ) => api.patch<ProductRecord>(`/products/${id}`, data),

  updateVariant: (
    variantId: string,
    data: {
      price?: number;
      wholesalePrice?: number | null;
      cashTransferPrice?: number | null;
      cardPaymentPrice?: number | null;
      extraFinancingPrice?: number | null;
      cost?: number;
      /** null / "" clears */
      size?: string | null;
      color?: string | null;
      barcode?: string | null;
      reference?: string | null;
      name?: string | null;
      serializedTracking?: boolean;
      priceTiers?: Array<{
        minQty: number;
        unitPrice: number;
        priority?: number;
      }>;
    },
  ) => api.patch(`/products/variants/${variantId}`, data),

  uploadImage: async (file: File | Blob, filename = "foto.jpg"): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append("file", file, file instanceof File ? file.name : filename);
    return api.requestFormData<{ url: string }>(
      "/products/upload-image",
      formData,
    );
  },

  addVariants: (
    productId: string,
    variants: Array<{
      sku: string;
      name?: string;
      size?: string;
      color?: string;
      cost: number;
      price: number;
      wholesalePrice?: number;
      cashTransferPrice?: number;
      cardPaymentPrice?: number;
      extraFinancingPrice?: number;
      serializedTracking?: boolean;
      barcode?: string;
      reference?: string;
      priceTiers?: Array<{ minQty: number; unitPrice: number; priority?: number }>;
    }>,
  ) => api.post<ProductRecord[]>(`/products/${productId}/variants/bulk`, variants),

  archiveOrDelete: (
    productId: string,
    payload: { action: 'archive' | 'delete' | 'unarchive'; reason: string },
  ) =>
    api.delete<{ success: boolean; action: string }>(`/products/${productId}`, payload),
};

// Sales API
export const salesApi = {
  getAll: async (page = 1, limit = 20) => {
    const response = await api.get<{
      data: Array<{
        id: string;
        number: number | null;
        total: number;
        status: string;
        createdAt: string;
        lines: Array<{
          quantity: number;
          unitPrice: number;
          variant: { product: { name: string } };
        }>;
      }>;
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>(`/sales?page=${page}&limit=${limit}`);
    return response.data || [];
  },

  create: (data: {
    lines: Array<{ variantId: string; quantity: number }>;
    contactId?: string;
  }) => api.post<{ id: string; total: number; status: string }>("/sales", data),

  pay: (
    saleId: string,
    data: { type: "CASH" | "CARD" | "TRANSFER"; amount: number },
  ) => api.post(`/sales/${saleId}/pay`, data),
};

// Reports API
export const reportsApi = {
  /** Dashboard KPIs for one store, or every store the user may read (storeId omitted). */
  getScopedDashboard: (storeId?: string, days: number = 30) => {
    const p = new URLSearchParams({ days: String(days) });
    if (storeId) p.append("storeId", storeId);
    return api.get<ScopedDashboardResponse>(`/reports/dashboard/scope?${p}`);
  },

  /** Paid sales of the business week (Mon key) in the same scope as getScopedDashboard. */
  getScopedWeekSales: (weekStart: string, storeId?: string) => {
    const p = new URLSearchParams({ weekStart });
    if (storeId) p.append("storeId", storeId);
    return api.get<DashboardWeekSalesResponse>(`/reports/dashboard/week-sales?${p}`);
  },

  getDashboard: () =>
    api.get<{
      totalSales: number;
      salesCount: number;
      lowStockCount: number;
      date: string;
    }>("/reports/dashboard/today"),

  // Dashboard metrics with range (30 days by default)
  getDashboardMetrics: (days: number = 30) =>
    api.get<{
      salesTotal: number;
      salesCount: number;
      salesToday: number;
      avgTicket: number;
      lowStockCount: number;
      rangeDays: number;
    }>(`/reports/dashboard?days=${days}`),

  // Low stock items list
  getLowStock: () =>
    api.get<
      Array<{
        id: string;
        sku: string;
        plu?: string;
        barcode?: string;
        name: string;
        size?: string;
        color?: string;
        stock: number;
        minStock: number;
      }>
    >("/reports/low-stock"),

  getSummary: (from: string, to: string) =>
    api.get<{
      range: { from: string; to: string };
      totals: {
        salesCount: number;
        grossSales: number;
        netSales: number;
        avgTicket: number;
        totalItems: number;
      };
    }>(`/reports/sales/summary?from=${from}&to=${to}`),

  getProfitability: (from: string, to: string) =>
    api.get<{
      totalRevenue: number;
      totalCost: number;
      grossProfit: number;
      margin: number;
      saleCount: number;
    }>(`/reports/profitability?startDate=${from}&endDate=${to}`),

  getByProduct: (from: string, to: string) =>
    api.get<
      Array<{
        productId: string;
        productName: string;
        skuOrBarcode: string;
        quantitySold: number;
        grossRevenue: number;
        avgPrice: number;
      }>
    >(`/reports/sales/by-product?from=${from}&to=${to}`),

  // ── V2 Analytics ──────────────────────────────────────────────────────────

  getV2Overview: (from: string, to: string, storeId?: string) => {
    const p = new URLSearchParams({ from, to });
    if (storeId && storeId !== "ALL") p.append("storeId", storeId);
    return api.get<{
      grossSales: number;
      totalDiscounts: number;
      netSales: number;
      voidsAndReturns: number;
      cogs: number;
      estimatedProfit: number;
      marginPercentage: number;
      totalOrders: number;
      averageTicket: number;
      totalItems: number;
      trends: { salesGrowth: number; ordersGrowth: number };
    }>(`/reports/v2/overview?${p}`);
  },

  getV2Trend: (from: string, to: string, storeId?: string) => {
    const p = new URLSearchParams({ from, to });
    if (storeId && storeId !== "ALL") p.append("storeId", storeId);
    return api.get<Array<{ date: string; amount: number }>>(`/reports/v2/trend?${p}`);
  },

  getV2DeepTrends: (from: string, to: string, storeId?: string) => {
    const p = new URLSearchParams({ from, to });
    if (storeId && storeId !== "ALL") p.append("storeId", storeId);
    return api.get<{
      highlights: { bestDay: string; bestHour: string; dailyAverage: number; peakRange: string };
      hourly: Array<{ hour: number; amount: number; orders: number }>;
      daily: Array<{ day: number; amount: number; orders: number }>;
    }>(`/reports/v2/deep-trends?${p}`);
  },

  getV2ProductsPerformance: (from: string, to: string, storeId?: string) => {
    const p = new URLSearchParams({ from, to });
    if (storeId && storeId !== "ALL") p.append("storeId", storeId);
    return api.get<
      Array<{
        id: string; rank: number; name: string; sku: string;
        category: string; supplierName: string; isConsignment: boolean;
        quantity: number; revenue: number; profitEstimate: number;
        marginPercent: number; participationPercent: number;
      }>
    >(`/reports/v2/products-performance?${p}`);
  },

  getV2CategoriesPerformance: (from: string, to: string, storeId?: string) => {
    const p = new URLSearchParams({ from, to });
    if (storeId && storeId !== "ALL") p.append("storeId", storeId);
    return api.get<
      Array<{
        category: string; quantity: number; revenue: number;
        ticketsCount: number; averageTicket: number; participationPercent: number;
      }>
    >(`/reports/v2/categories-performance?${p}`);
  },

  getV2PaymentMethodsDeep: (from: string, to: string, storeId?: string) => {
    const p = new URLSearchParams({ from, to });
    if (storeId && storeId !== "ALL") p.append("storeId", storeId);
    return api.get<{
      byMethod: Array<{
        type: string; totalAmount: number; transactionsCount: number;
        percentage: number; averageTicket: number;
      }>;
      cardProcessorBreakdown: Array<{
        processor: string; totalAmount: number; transactionsCount: number; percentage: number;
      }>;
    }>(`/reports/v2/payment-methods-deep?${p}`);
  },

  getV2VoidsReturns: (from: string, to: string, storeId?: string) => {
    const p = new URLSearchParams({ from, to });
    if (storeId && storeId !== "ALL") p.append("storeId", storeId);
    return api.get<{
      kpis: { totalLost: number; totalNetSales: number; leakageRate: number; transactionsCount: number; averageLoss: number };
      voidsAmount: number;
      returnsAmount: number;
      topReasons: Array<{ reason: string; amount: number; count: number }>;
      topReturnedProducts: Array<{ name: string; quantity: number; amount: number }>;
    }>(`/reports/v2/voids-returns?${p}`);
  },

  getV2DiscountsImpact: (from: string, to: string, storeId?: string) => {
    const p = new URLSearchParams({ from, to });
    if (storeId && storeId !== "ALL") p.append("storeId", storeId);
    return api.get<{
      kpis: { totalDiscountedAmount: number; discountedTicketsCount: number; averageDiscountPerTicket: number; sacrificedMarginPercent: number };
      reasons: Array<{ reason: string; count: number }>;
      trendChart: Array<{ date: string; grossSales: number; discounts: number }>;
    }>(`/reports/v2/discounts-impact?${p}`);
  },

  getV2ProfitabilityDeep: (from: string, to: string, storeId?: string) => {
    const p = new URLSearchParams({ from, to });
    if (storeId && storeId !== "ALL") p.append("storeId", storeId);
    return api.get<{
      summary: {
        grossSales: number;
        discounts: number;
        returns: number;
        netSales: number;
        cogs: number;
        grossProfit: number;
        marginPercent: number;
        transactions: number;
        averageTicket: number;
        itemsSold: number;
        missingCostProducts: number;
        negativeMarginProducts: number;
      };
      byProduct: Array<{
        productId: string;
        variantId: string;
        name: string;
        sku: string;
        category: string;
        storeName: string;
        quantity: number;
        netSales: number;
        cogs: number;
        grossProfit: number;
        marginPercent: number;
        missingCost: boolean;
        negativeMargin: boolean;
      }>;
      byCategory: Array<{
        category: string;
        quantity: number;
        netSales: number;
        cogs: number;
        grossProfit: number;
        marginPercent: number;
        transactions: number;
        averageTicket: number;
        missingCostProducts: number;
      }>;
      bySupplier: Array<{
        supplierId: string | null;
        supplierName: string;
        quantity: number;
        netSales: number;
        cogs: number;
        grossProfit: number;
        marginPercent: number;
        transactions: number;
        averageTicket: number;
        missingCostProducts: number;
      }>;
      byStore: Array<{
        storeId: string | null;
        storeName: string;
        quantity: number;
        netSales: number;
        cogs: number;
        grossProfit: number;
        marginPercent: number;
        transactions: number;
        averageTicket: number;
      }>;
      byPaymentMethod: Array<{
        method: string;
        collectedAmount: number;
        netSales: number;
        cogs: number;
        grossProfit: number;
        marginPercent: number;
        transactions: number;
        averageTicket: number;
        participationPercent: number;
      }>;
      alerts: {
        mostProfitableProducts: Array<{
          variantId: string;
          name: string;
          category: string;
          grossProfit: number;
        }>;
        lowestMarginProducts: Array<{
          variantId: string;
          name: string;
          category: string;
          marginPercent: number;
        }>;
        missingCostProducts: Array<{
          variantId: string;
          name: string;
        }>;
        negativeMarginProducts: Array<{
          variantId: string;
          name: string;
          marginPercent: number;
        }>;
      };
      meta: {
        paymentMethodAllocation: string;
      };
    }>(`/reports/v2/profitability-deep?${p}`);
  },

  getV2NetProfitSummary: (from: string, to: string, storeId?: string) => {
    const p = new URLSearchParams({ from, to });
    if (storeId && storeId !== "ALL") p.append("storeId", storeId);
    return api.get<{
      period: { from: string; to: string };
      netSales: number;
      cogs: number;
      repairCost: number;
      grossProfit: number;
      bankCommission: number;
      netProfit: number;
      operatingExpenses: number;
      realProfit: number;
      realMarginPercent: number;
    }>(`/reports/v2/net-profit-summary?${p}`);
  },

  exportV2ProfitabilityCsv: (from: string, to: string, storeId?: string) => {
    const p = new URLSearchParams({ from, to });
    if (storeId && storeId !== "ALL") p.append("storeId", storeId);
    return api.getBlob(`/reports/v2/profitability-export?${p}`);
  },

  getV2InventoryValuation: (storeId?: string) => {
    const p = new URLSearchParams();
    if (storeId && storeId !== "ALL") p.append("storeId", storeId);
    const qs = p.toString();
    return api.get<{
      summary: {
        totalInvestment: number;
        totalPotentialSale: number;
        totalPotentialProfit: number;
        potentialMarginPercent: number;
        skusWithStock: number;
        unitsInStock: number;
        missingCostCount: number;
        belowCostCount: number;
      };
      rows: Array<{
        variantId: string;
        productId: string;
        productName: string;
        sku: string;
        category: string;
        supplierId: string | null;
        supplierName: string;
        unitCost: number;
        unitPrice: number;
        currentStock: number;
        investmentValue: number;
        potentialSaleValue: number;
        potentialProfit: number;
        potentialMarginPercent: number;
        missingCost: boolean;
        belowCost: boolean;
      }>;
    }>(qs ? `/reports/v2/inventory-valuation?${qs}` : "/reports/v2/inventory-valuation");
  },

  exportV2InventoryValuationCsv: (storeId?: string) => {
    const p = new URLSearchParams();
    if (storeId && storeId !== "ALL") p.append("storeId", storeId);
    const qs = p.toString();
    return api.getBlob(qs ? `/reports/v2/inventory-valuation/export-csv?${qs}` : "/reports/v2/inventory-valuation/export-csv");
  },

  getV2Expirations: (storeId?: string) => {
    const p = new URLSearchParams();
    if (storeId && storeId !== "ALL") p.append("storeId", storeId);
    const qs = p.toString();
    return api.get<{
      kpis: { expiredBatches: number; expiredValue: number; expiringIn7Days: number; valueIn7Days: number; expiringIn30Days: number; valueIn30Days: number };
      details: Array<{ batchId: string; productName: string; variantName: string; warehouseName: string; quantity: number; unitCost: number; expiryAt: string; status: "EXPIRED" | "SEVEN_DAYS" | "THIRTY_DAYS"; lostValue: number }>;
    }>(qs ? `/reports/v2/expirations?${qs}` : "/reports/v2/expirations");
  },

  getV2NoMovement: (storeId?: string, days?: number) => {
    const p = new URLSearchParams();
    if (storeId && storeId !== "ALL") p.append("storeId", storeId);
    if (days) p.append("days", String(days));
    const qs = p.toString();
    return api.get<{
      days: number;
      items: Array<{
        variantId: string;
        productName: string;
        sku: string | null;
        category: string | null;
        currentStock: number;
        storeId: string | null;
        storeName: string | null;
        lastSaleAt: string | null;
        daysWithoutMovement: number | null;
      }>;
    }>(qs ? `/reports/v2/no-movement?${qs}` : "/reports/v2/no-movement");
  },

  getPhonesImeiSold: (from: string, to: string, storeId?: string) => {
    const p = new URLSearchParams({ from, to });
    if (storeId && storeId !== "ALL") p.append("storeId", storeId);
    return api.get<
      Array<{
        imei1: string; imei2: string | null; serialNumber: string | null;
        productName: string; sku: string; category: string | null;
        storeId: string | null; storeName: string | null;
        saleNumber: number; soldAt: string;
        unitPrice: number; unitCost: number | null; margin: number | null; marginPercent: number | null;
        priceType: string;
      }>
    >(`/reports/phones/imei-sold?${p.toString()}`);
  },

  getConsignmentReport: (params: {
    from?: string; to?: string; storeId?: string;
    supplierId?: string; categoryId?: string; search?: string;
    settlementFilter?: "all" | "pending" | "settled";
  }) => {
    const p = new URLSearchParams();
    if (params.from) p.append("from", params.from);
    if (params.to) p.append("to", params.to);
    if (params.storeId && params.storeId !== "ALL") p.append("storeId", params.storeId);
    if (params.supplierId) p.append("supplierId", params.supplierId);
    if (params.categoryId) p.append("categoryId", params.categoryId);
    if (params.search) p.append("search", params.search);
    if (params.settlementFilter && params.settlementFilter !== "all")
      p.append("settlementFilter", params.settlementFilter);
    return api.get<{
      summary: {
        totalUnitsSold: number;
        totalRevenue: number;
        totalCogsSold: number;
        totalProfit: number;
        totalCurrentStock: number;
        totalStockCostRemaining: number;
        totalSkus: number;
        missingDataAlerts: { missingSupplier: number; missingCost: number };
      };
      rows: Array<{
        variantId: string; productId: string; productName: string; sku: string;
        category: string; supplierId: string | null; supplierName: string;
        entryDate: string | null; unitCost: number; unitPrice: number;
        qtySold: number; revenue: number; cogsSold: number; profit: number;
        currentStock: number; stockCostRemaining: number;
        missingSupplier: boolean; missingCost: boolean;
      }>;
    }>(`/reports/v2/consignments?${p}`);
  },

  exportConsignmentPdfProvider: (params: {
    from?: string; to?: string; storeId?: string;
    supplierId?: string; categoryId?: string; search?: string;
  }) => {
    const p = new URLSearchParams();
    if (params.from) p.append("from", params.from);
    if (params.to) p.append("to", params.to);
    if (params.storeId && params.storeId !== "ALL") p.append("storeId", params.storeId);
    if (params.supplierId) p.append("supplierId", params.supplierId);
    if (params.categoryId) p.append("categoryId", params.categoryId);
    if (params.search) p.append("search", params.search);
    return api.getBlob(`/reports/v2/consignments/export/pdf-provider?${p}`);
  },

  exportConsignmentCsvProvider: (params: {
    from?: string; to?: string; storeId?: string;
    supplierId?: string; categoryId?: string; search?: string;
  }) => {
    const p = new URLSearchParams();
    if (params.from) p.append("from", params.from);
    if (params.to) p.append("to", params.to);
    if (params.storeId && params.storeId !== "ALL") p.append("storeId", params.storeId);
    if (params.supplierId) p.append("supplierId", params.supplierId);
    if (params.categoryId) p.append("categoryId", params.categoryId);
    if (params.search) p.append("search", params.search);
    return api.getBlob(`/reports/v2/consignments/export/csv-provider?${p}`);
  },

  exportConsignmentCsvInternal: (params: {
    from?: string; to?: string; storeId?: string;
    supplierId?: string; categoryId?: string; search?: string;
  }) => {
    const p = new URLSearchParams();
    if (params.from) p.append("from", params.from);
    if (params.to) p.append("to", params.to);
    if (params.storeId && params.storeId !== "ALL") p.append("storeId", params.storeId);
    if (params.supplierId) p.append("supplierId", params.supplierId);
    if (params.categoryId) p.append("categoryId", params.categoryId);
    if (params.search) p.append("search", params.search);
    return api.getBlob(`/reports/v2/consignments/export/csv-internal?${p}`);
  },
};

// ── Consignment Settlements API ────────────────────────────────────────────

export interface ConsignmentSettlement {
  id: string;
  settlementNumber: string;
  supplierId: string;
  supplierName: string;
  periodFrom: string;
  periodTo: string;
  status: "CONFIRMED" | "CANCELLED";
  totalSold: number;
  totalProviderCost: number;
  totalStoreProfit?: number;
  totalUnitsSold: number;
  createdAt: string;
  createdBy: string;
}

export interface ConsignmentSettlementDetail extends ConsignmentSettlement {
  cancelledAt: string | null;
  notes: string | null;
  lines: Array<{
    id: string;
    skuSnapshot: string;
    productNameSnapshot: string;
    variantNameSnapshot: string | null;
    quantity: number;
    unitSalePriceSnapshot: number;
    unitCostSnapshot: number;
    totalSold: number;
    totalProviderCost: number;
    storeProfit?: number;
  }>;
}

export const settlementsApi = {
  create: (body: {
    supplierId: string;
    from?: string;
    to?: string;
    storeId?: string;
    notes?: string;
  }) => api.post<{ id: string; settlementNumber: string; supplierName: string; linesCount: number; totalProviderCost: number }>("/reports/v2/consignments/settlements", body),

  list: (params: {
    supplierId?: string;
    storeId?: string;
    page?: number;
    limit?: number;
  }) => {
    const p = new URLSearchParams();
    if (params.supplierId) p.append("supplierId", params.supplierId);
    if (params.storeId && params.storeId !== "ALL") p.append("storeId", params.storeId);
    if (params.page) p.append("page", String(params.page));
    if (params.limit) p.append("limit", String(params.limit));
    return api.get<{ data: ConsignmentSettlement[]; total: number; page: number; limit: number; totalPages: number }>(
      `/reports/v2/consignments/settlements?${p}`,
    );
  },

  getById: (id: string) =>
    api.get<ConsignmentSettlementDetail>(`/reports/v2/consignments/settlements/${id}`),

  exportPdfProvider: (id: string) =>
    api.getBlob(`/reports/v2/consignments/settlements/${id}/export/pdf-provider`),

  exportCsvProvider: (id: string) =>
    api.getBlob(`/reports/v2/consignments/settlements/${id}/export/csv-provider`),

  exportCsvInternal: (id: string) =>
    api.getBlob(`/reports/v2/consignments/settlements/${id}/export/csv-internal`),
};

// Notifications API
export interface Notification {
  id: string;
  type: string;
  message: string;
  title?: string;
  level: "warning" | "critical" | "info";
  timestamp: string;
  readAt?: string | null;
  metadata?: Record<string, any>;
  // Stock alert fields
  productId?: string;
  variantId?: string;
  stock?: number;
  minStock?: number;
}

export const notificationsApi = {
  getAll: () => api.get<Notification[]>("/notifications"),

  getCount: () =>
    api.get<{
      count: number;
      persistedCount?: number;
      stockAlertCount?: number;
    }>("/notifications/count"),

  getNegativeStock: (filters?: { storeId?: string; warehouseId?: string }) => {
    const params = new URLSearchParams();
    if (filters?.storeId) params.append("storeId", filters.storeId);
    if (filters?.warehouseId) params.append("warehouseId", filters.warehouseId);
    const suffix = params.toString() ? `?${params.toString()}` : "";

    return api.get<{
      items: Array<{
        variantId: string;
        sku: string;
        productName: string;
        warehouseId: string;
        warehouseName: string;
        storeId?: string | null;
        storeName?: string | null;
        currentStock: number;
      }>;
      total: number;
    }>(`/notifications/negative-stock${suffix}`);
  },

  markRead: (id: string) => api.post(`/notifications/${id}/read`),

  markAllRead: () => api.post("/notifications/read-all"),
};

export type InternalAlertSeverity = "INFO" | "WARNING" | "HIGH" | "CRITICAL";
export type InternalAlertStatus =
  | "PENDING"
  | "APPROVED"
  | "REJECTED"
  | "ATTENDED";

export interface InternalAlert {
  id: string;
  tenantId: string;
  storeId?: string | null;
  store?: { id: string; name: string } | null;
  type: string;
  title: string;
  message: string;
  severity: InternalAlertSeverity;
  status: InternalAlertStatus;
  isRead: boolean;
  readAt?: string | null;
  actorUserId?: string | null;
  actorRole?: string | null;
  actorUser?: { id: string; name: string; email: string } | null;
  targetRole?: string | null;
  targetUserId?: string | null;
  targetUser?: { id: string; name: string; email: string } | null;
  relatedEntityType?: string | null;
  relatedEntityId?: string | null;
  metadata?: Record<string, any> | null;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string | null;
}

export interface InternalAlertListResponse {
  items: InternalAlert[];
  total: number;
  unreadCount: number;
  pendingCount: number;
}

export interface InternalAlertCountResponse {
  total: number;
  unreadCount: number;
  pendingCount: number;
}

export const internalAlertsApi = {
  list: (filters?: {
    status?: InternalAlertStatus | InternalAlertStatus[];
    type?: string | string[];
    unreadOnly?: boolean;
    storeId?: string;
    limit?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.status) {
      const value = Array.isArray(filters.status)
        ? filters.status.join(",")
        : filters.status;
      params.set("status", value);
    }
    if (filters?.type) {
      const value = Array.isArray(filters.type)
        ? filters.type.join(",")
        : filters.type;
      params.set("type", value);
    }
    if (filters?.unreadOnly) {
      params.set("unreadOnly", "true");
    }
    if (filters?.storeId) {
      params.set("storeId", filters.storeId);
    }
    if (filters?.limit) {
      params.set("limit", String(filters.limit));
    }
    return api.get<InternalAlertListResponse>(
      `/internal-alerts${params.toString() ? `?${params.toString()}` : ""}`,
    );
  },

  getCount: (filters?: { storeId?: string }) => {
    const params = new URLSearchParams();
    if (filters?.storeId) {
      params.set("storeId", filters.storeId);
    }
    return api.get<InternalAlertCountResponse>(
      `/internal-alerts/count${params.toString() ? `?${params.toString()}` : ""}`,
    );
  },

  markRead: (id: string) => api.post(`/internal-alerts/${id}/read`),

  markManyRead: (ids?: string[]) =>
    api.post("/internal-alerts/read", ids?.length ? { ids } : {}),

  scan: (storeId?: string) =>
    api.post<{ detected: number; created: number; skipped: boolean }>(
      "/internal-alerts/scan",
      storeId ? { storeId } : {},
    ),

  dismiss: (id: string) => api.patch(`/internal-alerts/${id}/dismiss`),
};

// Contacts API
export interface Contact {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  channel: string;
  type: string;
  isVip: boolean;
  idNumber?: string;
  address?: string;
  isCreditCustomer?: boolean;
  creditLimit?: number;
  currentBalance?: number;
  status?: string;
  typeChangeReason?: string;
}

export const suggestionsApi = {
  create: (data: { title: string; description: string }) =>
    api.post<{ id: string; createdAt: string }>("/suggestions", data),
};

export const contactsApi = {
  getAll: async (page = 1, limit = 50) => {
    const response = await api.get<{
      data: Contact[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>(`/contacts?page=${page}&limit=${limit}`);
    // Return array directly (backend returns { data, meta })
    return response.data || [];
  },

  getById: (id: string) => api.get<Contact>(`/contacts/${id}`),

  create: (data: Partial<Contact>) => api.post<Contact>("/contacts", data),

  update: (id: string, data: Partial<Contact>) =>
    api.patch<Contact>(`/contacts/${id}`, data),
};

// Conversations API
export const conversationsApi = {
  getAll: () =>
    api.get<
      Array<{
        id: string;
        contact: { name: string };
        channel: string;
        status: string;
        lastMessageAt: string;
      }>
    >("/conversations"),

  getMessages: (conversationId: string) =>
    api.get<
      Array<{
        id: string;
        body: string;
        direction: "IN" | "OUT";
        createdAt: string;
      }>
    >(`/conversations/${conversationId}/messages`),

  sendMessage: (conversationId: string, body: string) =>
    api.post(`/conversations/${conversationId}/messages`, { body }),
};

// Inventory API
export interface InventoryItem {
  id: string;
  sku: string;
  name: string;
  productName: string;
  categoryId?: string | null;
  categoryName?: string | null;
  categoryIsActive?: boolean | null;
  barcode?: string;
  plu?: string;
  sellUom: "UNIT" | "LB" | "L";
  price: number;
  cost?: number | null;
  minStock: number;
  expiryTrackingEnabled: boolean;
  stock: number;
  effectiveStock?: number;
  availableStock?: number;
  availableSerializedCount?: number;
  soldSerializedCount?: number;
  reservedSerializedCount?: number;
  serializedSummary?: {
    available: number;
    sold: number;
    reserved: number;
  };
  stockStatus?: "OUT_OF_STOCK" | "LOW_STOCK" | "OK";
  canSell?: boolean;
  isSerialized?: boolean;
  isLowStock: boolean;
  supplier?: string | null;
  /** "Por recibir": ordered to suppliers, not here yet (never part of the stock). */
  incoming?: number;
  /** "Quiero tener normalmente" for this store, when set. */
  targetQty?: number | null;
}

export const inventoryApi = {
  getItems: (filters?: { categoryId?: string; uncategorized?: boolean }) => {
    const params = new URLSearchParams();
    if (filters?.categoryId) params.set("categoryId", filters.categoryId);
    if (filters?.uncategorized) params.set("uncategorized", "true");
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return api.get<InventoryItem[]>(`/inventory/items${suffix}`);
  },

  lookup: (code: string) =>
    api.get<InventoryItem | null>(`/inventory/lookup?code=${code}`),

  getLowStock: () => api.get<InventoryItem[]>("/inventory/low-stock"),

  getAvailability: (
    warehouseIdOrFilters?:
      | string
      | {
          warehouseId?: string;
          search?: string;
          categoryId?: string;
          supplierId?: string;
          uncategorized?: boolean;
          consignment?: "all" | "consignment" | "owned";
        },
  ) => {
    const filters =
      typeof warehouseIdOrFilters === "string"
        ? { warehouseId: warehouseIdOrFilters }
        : warehouseIdOrFilters || {};
    const params = new URLSearchParams();
    if (filters.warehouseId) params.set("warehouseId", filters.warehouseId);
    if (filters.search) params.set("search", filters.search);
    if (filters.categoryId) params.set("categoryId", filters.categoryId);
    if (filters.supplierId) params.set("supplierId", filters.supplierId);
    if (filters.uncategorized) params.set("uncategorized", "true");
    if (filters.consignment && filters.consignment !== "all") {
      params.set("consignment", filters.consignment);
    }
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return api.get<
      Array<{
        variantId: string;
        sku: string;
        barcode?: string;
        productName: string;
        categoryId?: string | null;
        categoryName?: string | null;
        categoryIsActive?: boolean | null;
        isConsignment?: boolean;
        quantity: number;
        warehouseId?: string;
        warehouseName?: string;
        price?: number | string | null;
        cost?: number | string | null;
        supplierId?: string | null;
        supplier?: string | null;
        sellUom?: string;
        minStock?: number;
        effectiveStock?: number;
        availableStock?: number;
        availableSerializedCount?: number;
        soldSerializedCount?: number;
        reservedSerializedCount?: number;
        serializedSummary?: {
          available: number;
          sold: number;
          reserved: number;
        };
        stockStatus?: "OUT_OF_STOCK" | "LOW_STOCK" | "OK";
        canSell?: boolean;
        isSerialized?: boolean;
      }>
    >(`/inventory/availability${suffix}`);
  },

  exportAvailabilityCsv: (filters?: {
    storeId?: string;
    warehouseId?: string;
    search?: string;
    categoryId?: string;
    supplierId?: string;
    uncategorized?: boolean;
    consignment?: "all" | "consignment" | "owned";
    startDate?: string;
    endDate?: string;
  }) => {
    const params = new URLSearchParams();
    if (filters?.storeId) params.append("storeId", filters.storeId);
    if (filters?.warehouseId) params.append("warehouseId", filters.warehouseId);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.categoryId) params.append("categoryId", filters.categoryId);
    if (filters?.supplierId) params.append("supplierId", filters.supplierId);
    if (filters?.uncategorized) params.append("uncategorized", "true");
    if (filters?.consignment && filters.consignment !== "all") {
      params.append("consignment", filters.consignment);
    }
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return api.getBlob(`/inventory/export/csv${suffix}`);
  },

  getWarehouses: (tenantOverrideId?: string) =>
    api.get<
      Array<{
        id: string;
        name: string;
        location: string;
        type?: "STORE" | "WAREHOUSE" | "BRANCH" | "CENTRAL";
        createdAt?: string;
        storeId?: string | null;
        storeName?: string;
        store?: { id: string; name: string; defaultWarehouseId?: string | null } | null;
      }>
    >("/inventory/warehouses", {
      headers: buildTenantOverrideHeaders(tenantOverrideId),
    }),

  receiveBatch: (data: {
    warehouseId: string;
    supplierId?: string;
    reference?: string;
    /** PURCHASE (default, creates a Purchase) | OPENING (initial stock, no Purchase). */
    operationType?: "PURCHASE" | "OPENING";
    /** Idempotency key: retries with the same value never double the stock. */
    operationId?: string;
    items: Array<{
      variantId: string;
      quantity?: number;
      unitCost?: number;
      // Buy by presentation: "5 ristras at L60" — the backend derives the base
      // units (5×12=60) and the per-base cost (L5). See presentation flow.
      presentationId?: string;
      presentationQty?: number;
      presentationUnitCost?: number;
      expiryAt?: string;
    }>;
  }) => api.post("/inventory/receive-batch", data),

  adjustStock: (data: {
    variantId: string;
    warehouseId: string;
    mode: "SET" | "DELTA";
    delta?: number;
    quantity?: number;
    reasonCode: string;
    reasonText: string;
  }) => api.post("/inventory/adjust", data),

  // Consolidate getMovements (Legacy) -> getLedger (New)
  getMovements: (variantId?: string, warehouseId?: string) =>
    api.get(
      `/inventory/movements${variantId || warehouseId ? "?" : ""}${variantId ? `variantId=${variantId}` : ""}${warehouseId ? `&warehouseId=${warehouseId}` : ""}`,
    ),

  // New Ledger-First API
  getBalances: ({
    storeId,
    search,
    page = 1,
    limit = 20,
  }: {
    storeId?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (storeId) params.append("storeId", storeId);
    if (search) params.append("search", search);
    return api.get<{
      data: any[]; // Typed in hooks/components
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>(`/inventory/balance?${params.toString()}`);
  },

  getLedger: ({
    storeId,
    variantId,
    batchId,
    from,
    to,
    page = 1,
    limit = 20,
  }: {
    storeId?: string;
    variantId: string;
    batchId?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
  }) => {
    const params = new URLSearchParams({
      variantId,
      page: page.toString(),
      limit: limit.toString(),
    });
    if (storeId) params.append("storeId", storeId);
    if (batchId) params.append("batchId", batchId);
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    return api.get<{
      data: any[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>(`/inventory/ledger?${params.toString()}`);
  },

  // Kardex Report (full audit trail)
  getKardex: ({
    variantId,
    warehouseId,
    startDate,
    endDate,
  }: {
    variantId: string;
    warehouseId?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const params = new URLSearchParams({ variantId });
    if (warehouseId) params.append("warehouseId", warehouseId);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    return api.get<{
      product: {
        variantId: string;
        sku: string;
        name: string;
        supplier: string | null;
      };
      entries: Array<{
        id: string;
        date: string;
        type: string;
        quantity: string;
        balanceBefore: string;
        balanceAfter: string;
        reason: string | null;
        referenceId: string;
        warehouseName: string;
        user: string;
      }>;
      filters: {
        warehouseId: string | null;
        startDate: string | null;
        endDate: string | null;
      };
      generatedAt: string;
    }>(`/inventory/kardex?${params.toString()}`);
  },

  /** READ-ONLY: where else this product is (never used to sell). */
  getStockLookup: (variantId: string) =>
    api.get<StockLookupResponse>(`/inventory/stock-lookup?variantId=${encodeURIComponent(variantId)}`),

  /** Producto | Total | por ubicación, within the user's read scope. */
  getStockByLocation: (opts: { storeId?: string; search?: string; categoryId?: string; page?: number; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (opts.storeId) p.append("storeId", opts.storeId);
    if (opts.search) p.append("search", opts.search);
    if (opts.categoryId) p.append("categoryId", opts.categoryId);
    if (opts.page) p.append("page", String(opts.page));
    if (opts.limit) p.append("limit", String(opts.limit));
    return api.get<StockByLocationResponse>(`/inventory/stock-by-location?${p}`);
  },

  // Phase 9: Multi-branch
  getGlobalBalance: (search?: string) => {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    return api.get<
      Array<{
        warehouseId: string;
        warehouseName: string;
        storeName: string;
        variantId: string;
        sku: string;
        barcode?: string;
        productName: string;
        qty: number;
      }>
    >(`/inventory/balance/global?${params.toString()}`);
  },

  getUserWarehouses: (userId: string) =>
    api.get<
      Array<{
        id: string;
        warehouseId: string;
        warehouse: { id: string; name: string };
      }>
    >(`/inventory/user-warehouses/${userId}`),

  setUserWarehouses: (userId: string, warehouseIds: string[]) =>
    api.put(`/inventory/user-warehouses/${userId}`, { warehouseIds }),

  /** Warehouses this user may operate (OWNER: all active; others: assigned). */
  getAllowedWarehouses: (userId: string) =>
    api.get<Array<{ id: string; name: string; store?: { id: string; name: string } | null }>>(
      `/inventory/user-warehouses/${userId}`,
    ),

  // Transfers
  createTransfer: (data: {
    sourceWarehouseId: string;
    destWarehouseId: string;
    note?: string;
    lines: Array<{ variantId: string; quantity: number }>;
  }) => api.post<StockTransfer>("/inventory/transfers", data),

  executeTransfer: (data: {
    sourceWarehouseId: string;
    destWarehouseId: string;
    note?: string;
    lines: Array<{ variantId: string; quantity: number }>;
  }) => api.post<StockTransfer>("/inventory/transfers/execute", data),

  /** Move specific IMEI / serial units (the same physical units) between warehouses. */
  transferSerializedUnits: (data: {
    sourceWarehouseId: string;
    destWarehouseId: string;
    variantId: string;
    unitIds: string[];
    operationId: string;
    note?: string;
  }) =>
    api.post<{
      transferId: string;
      idempotentReplay: boolean;
      count: number;
      sourceWarehouse: { id: string; name: string };
      destWarehouse: { id: string; name: string };
      units: Array<{ id: string; imei1: string | null; imei2: string | null; serialNumber: string | null }>;
    }>("/inventory/transfers/serialized", data),

  getTransfers: () => api.get<StockTransfer[]>("/inventory/transfers"),

  getTransfer: (id: string) =>
    api.get<StockTransfer>(`/inventory/transfers/${id}`),

  submitTransfer: (id: string) =>
    api.post<StockTransfer>(`/inventory/transfers/${id}/submit`),

  approveTransfer: (id: string) =>
    api.post(`/inventory/transfers/${id}/approve`),

  rejectTransfer: (id: string) =>
    api.post<StockTransfer>(`/inventory/transfers/${id}/reject`),

  getVariantBatches: (variantId: string, limit = 20) => {
    const params = new URLSearchParams({ variantId, limit: String(limit) });
    return api.get<{
      data: Array<{
        id: string;
        variantId: string;
        sku: string;
        productName: string;
        warehouseName: string;
        supplierId: string | null;
        qtyReceived: number;
        qtyRemaining: number;
        unitCost: number;
        receivedAt: string;
        expiresAt: string | null;
        status: "ACTIVE" | "DEPLETED" | "EXPIRED";
      }>;
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>(`/inventory/batches?${params.toString()}`);
  },
};

export type InventoryImportFieldKey =
  | "productName"
  | "sku"
  | "categoryName"
  | "retailPrice"
  | "wholesalePrice"
  | "cost"
  | "stock"
  | "barcode"
  | "description"
  | "variantName"
  | "color"
  | "size";

export interface InventoryImportFieldDefinition {
  key: InventoryImportFieldKey;
  label: string;
  required: boolean;
  description: string;
}

export interface InventoryImportMapping {
  productName?: string;
  sku?: string;
  categoryName?: string;
  retailPrice?: string;
  wholesalePrice?: string;
  cost?: string;
  stock?: string;
  barcode?: string;
  description?: string;
  variantName?: string;
  color?: string;
  size?: string;
}

export type InventoryImportRowOverride = Partial<
  Record<InventoryImportFieldKey, string>
>;

export interface InventoryImportUploadResponse {
  sessionId: string;
  fileName: string;
  delimiter: string;
  rowCount: number;
  headers: string[];
  sampleRows: Array<Record<string, string>>;
  suggestedMapping: InventoryImportMapping;
  strategy: "CREATE_ONLY";
  expiresAt: string;
  fieldDefinitions: InventoryImportFieldDefinition[];
}

export interface InventoryImportPreviewRow {
  rowNumber: number;
  status: "valid" | "error" | "conflict";
  sku: string | null;
  productName: string | null;
  categoryName: string | null;
  retailPrice: number | null;
  stock: number | null;
  messages: Array<{
    code: string;
    field?: InventoryImportFieldKey;
    severity: "error" | "conflict" | "warning";
    message: string;
  }>;
  raw: Record<string, string>;
  normalized: Record<string, unknown>;
}

export interface InventoryImportPreviewResponse {
  sessionId: string;
  fileName: string;
  rowCount: number;
  headers: string[];
  sampleRows: Array<Record<string, string>>;
  mapping: InventoryImportMapping;
  options: {
    warehouseId: string;
    targetContext?: {
      warehouseId: string;
      warehouseName: string;
      storeId: string | null;
      storeName: string | null;
    };
    createMissingCategories: boolean;
    categoriesToCreate?: string[];
    importMode: "CREATE_ONLY";
    rowOverrides?: Record<string, InventoryImportRowOverride>;
  };
  strategy: "CREATE_ONLY";
  summary: {
    totalRows: number;
    validRows: number;
    errorRows: number;
    conflictRows: number;
    canImport: boolean;
    estimatedProductsToCreate: number;
    estimatedVariantsToCreate: number;
    /** Rows that are items ALREADY in the catalog: only their stock is added. */
    existingVariantsToStock?: number;
    missingCategories: string[];
    categoriesToCreate: string[];
    issueCounts: Record<string, number>;
    warehouseId: string;
    warehouseName?: string | null;
    storeId?: string | null;
    storeName?: string | null;
    strategy: "CREATE_ONLY";
    editedRows?: number;
  };
  validRowsPreview: InventoryImportPreviewRow[];
  errorRows: InventoryImportPreviewRow[];
  conflictRows: InventoryImportPreviewRow[];
  missingCategories: Array<{
    name: string;
    willCreate: boolean;
  }>;
  fieldDefinitions: InventoryImportFieldDefinition[];
}

export interface InventoryImportConfirmResponse {
  sessionId: string;
  fileName: string;
  status: "PROCESSING";
  dispatchMode: "queue" | "inline-fallback";
  queuedAt: string;
  message: string;
}

export interface InventoryImportCompletedResult {
  sessionId: string;
  fileName: string;
  strategy: "CREATE_ONLY";
  summary: {
    totalRows: number;
    validRowsDetected: number;
    importedRows: number;
    importedProducts: number;
    importedVariants: number;
    existingVariantsStocked?: number;
    stockAppliedRows: number;
    skippedErrorRows: number;
    skippedConflictRows: number;
    skippedFailedRows: number;
    createdCategories: number;
    createdCategoryNames: string[];
    warehouseId: string;
    warehouseName?: string | null;
    storeId?: string | null;
    storeName?: string | null;
    strategy: "CREATE_ONLY";
  };
  importedRowsPreview: Array<{
    rowNumber: number;
    productId: string;
    variantId: string;
    productName: string;
    sku: string;
    stockImported: number;
  }>;
  skippedRows: Array<{
    rowNumber: number;
    status: "error" | "conflict" | "failed";
    sku: string | null;
    productName: string | null;
    messages: string[];
  }>;
}

export interface InventoryImportHistoryEntry {
  id: string;
  fileName: string;
  rowCount: number;
  status:
    | "UPLOADED"
    | "PREVIEWED"
    | "PROCESSING"
    | "IMPORTED"
    | "FAILED"
    | "EXPIRED";
  createdAt: string;
  updatedAt: string;
  importedAt: string | null;
  expiresAt: string;
  previewSummary: Record<string, unknown> | null;
  resultSummary: Record<string, unknown> | null;
  createdBy: {
    id: string;
    name: string;
    email: string | null;
  };
}

export interface InventoryImportStatusResponse {
  sessionId: string;
  fileName: string;
  rowCount: number;
  status:
    | "UPLOADED"
    | "PREVIEWED"
    | "PROCESSING"
    | "IMPORTED"
    | "FAILED"
    | "EXPIRED";
  createdAt: string;
  updatedAt: string;
  importedAt: string | null;
  expiresAt: string;
  progress: {
    totalRows: number;
    validRowsDetected: number;
    processedValidRows: number;
    progressPct: number;
    phase: string | null;
    message: string | null;
  };
  result: InventoryImportCompletedResult | null;
  errorMessage: string | null;
}

export const inventoryImportApi = {
  uploadCsv: (file: File, tenantOverrideId?: string) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.requestFormData<InventoryImportUploadResponse>(
      "/inventory/imports/upload",
      formData,
      "POST",
      {
        headers: buildTenantOverrideHeaders(tenantOverrideId),
        timeoutMs: 45_000,
      },
    );
  },
  preview: (
    sessionId: string,
    data: {
      mapping: InventoryImportMapping;
      warehouseId: string;
      createMissingCategories?: boolean;
      categoriesToCreate?: string[];
      rowOverrides?: Record<string, InventoryImportRowOverride>;
    },
    tenantOverrideId?: string,
  ) =>
    api.post<InventoryImportPreviewResponse>(
      `/inventory/imports/${sessionId}/preview`,
      data,
      {
        headers: buildTenantOverrideHeaders(tenantOverrideId),
        timeoutMs: 45_000,
      },
    ),
  confirm: (sessionId: string, tenantOverrideId?: string) =>
    api.post<InventoryImportConfirmResponse>(
      `/inventory/imports/${sessionId}/confirm`,
      {},
      {
        headers: buildTenantOverrideHeaders(tenantOverrideId),
        timeoutMs: 60_000,
      },
    ),
  getStatus: (sessionId: string, tenantOverrideId?: string) =>
    api.get<InventoryImportStatusResponse>(
      `/inventory/imports/${sessionId}/status`,
      {
        headers: buildTenantOverrideHeaders(tenantOverrideId),
        timeoutMs: 20_000,
      },
    ),
  getHistory: (limit = 10, tenantOverrideId?: string) =>
    api.get<{ sessions: InventoryImportHistoryEntry[] }>(
      `/inventory/imports/history?limit=${limit}`,
      {
        headers: buildTenantOverrideHeaders(tenantOverrideId),
      },
    ),
  downloadTemplate: (tenantOverrideId?: string) =>
    api.get<Blob>("/inventory/imports/template", {
      headers: buildTenantOverrideHeaders(tenantOverrideId),
      responseType: "blob",
      timeoutMs: 45_000,
    }),
};

// Stock Transfer type
export interface StockTransfer {
  id: string;
  tenantId: string;
  sourceWarehouseId: string;
  destWarehouseId: string;
  status: "DRAFT" | "SUBMITTED" | "APPROVED" | "REJECTED" | "CANCELLED";
  createdById: string;
  approvedById?: string;
  createdAt: string;
  updatedAt: string;
  sourceWarehouse?: { id: string; name: string };
  destWarehouse?: { id: string; name: string };
  createdBy?: { firstName: string; lastName: string };
  approvedBy?: { firstName: string; lastName: string };
  lines?: Array<{
    id: string;
    variantId: string;
    quantity: number;
    variant?: { id: string; sku: string; product?: { name: string } };
  }>;
}

// Suppliers API
// ========== Purchasing & replenishment (optional layer) ==========

export type SupplierOrderStatus = "DRAFT" | "ORDERED" | "PARTIALLY_RECEIVED" | "RECEIVED" | "CANCELLED";

export interface ReplenishmentRow {
  storeId: string;
  storeName: string;
  variantId: string;
  productId: string;
  productName: string;
  variantLabel: string | null;
  sku: string;
  sellUom: string;
  serialized: boolean;
  onHand: number;
  alertAt: number;
  target: number | null;
  levelSource: "LOCATION" | "PRODUCT";
  incoming: number;
  projected: number;
  need: number | null;
  status: "OUT" | "LOW" | "COVERED" | "OK";
  transferOptions: Array<{ storeId: string | null; warehouseId: string | null; storeName: string; onHand: number; keepAt: number; canGive: number; suggested: number; canTransfer: boolean }>;
  usualSupplier: { id: string; name: string } | null;
}

export interface StockLevelsResponse {
  variantId: string;
  productName: string;
  variantLabel: string | null;
  sku: string;
  sellUom: string;
  productMinimum: number;
  stores: Array<{ storeId: string; storeName: string; onHand: number; alertAt: number | null; targetQty: number | null; source: "LOCATION" | "PRODUCT" | null }>;
}

export interface SupplierOrderSummary {
  id: string;
  number: number;
  status: SupplierOrderStatus;
  supplier: { id: string; name: string } | null;
  store: { id: string; name: string } | null;
  warehouse: { id: string; name: string };
  expectedAt: string | null;
  orderedAt: string | null;
  createdAt: string;
  lineCount: number;
  deliveries: number;
  expectedTotal: number;
  progress: number;
}

export interface SupplierOrderLine {
  id: string;
  variantId: string;
  productId: string;
  productName: string;
  variantLabel: string | null;
  sku: string;
  sellUom: string;
  serialized: boolean;
  presentationId: string | null;
  presentationName: string | null;
  presentationFactor: number;
  orderedQty: number;
  receivedQty: number;
  pendingQty: number;
  expectedUnitCost: number;
  realUnitCost: number | null;
}

export interface SupplierOrderDetail {
  id: string;
  number: number;
  status: SupplierOrderStatus;
  supplier: { id: string; name: string; phone?: string | null; email?: string | null } | null;
  store: { id: string; name: string } | null;
  warehouse: { id: string; name: string };
  orderedAt: string | null;
  expectedAt: string | null;
  notes: string | null;
  createdAt: string;
  lines: SupplierOrderLine[];
  deliveries: Array<{ id: string; receivedAt: string; notes: string | null; lines: Array<{ lineId: string; baseQty: number; unitCost: number; serialCount: number | null }> }>;
  totals: { expected: number; received: number };
}

export interface SupplierOrderInput {
  operationId?: string;
  supplierId?: string | null;
  warehouseId: string;
  expectedAt?: string | null;
  notes?: string | null;
  status?: "DRAFT" | "ORDERED";
  lines: Array<{ variantId: string; quantity: number; presentationId?: string | null; expectedUnitCost?: number | null }>;
}

export const purchasingApi = {
  replenishment: (storeId?: string) =>
    api.get<{ scope: { storeIds: string[] | null; global: boolean }; configured: boolean; summary: { out: number; low: number; covered: number }; rows: ReplenishmentRow[] }>(
      `/purchasing/replenishment${storeId ? `?storeId=${encodeURIComponent(storeId)}` : ""}`,
    ),
  getStockLevels: (variantId: string) =>
    api.get<StockLevelsResponse>(`/purchasing/stock-levels?variantId=${encodeURIComponent(variantId)}`),
  setStockLevels: (variantId: string, levels: Array<{ storeId: string; alertAt: number | null; targetQty?: number | null }>) =>
    api.put<StockLevelsResponse>("/purchasing/stock-levels", { variantId, levels }),
  listOrders: (q: { status?: string; storeId?: string } = {}) => {
    const p = new URLSearchParams();
    if (q.status) p.append("status", q.status);
    if (q.storeId) p.append("storeId", q.storeId);
    return api.get<SupplierOrderSummary[]>(`/purchasing/orders?${p}`);
  },
  getOrder: (id: string) => api.get<SupplierOrderDetail>(`/purchasing/orders/${id}`),
  openLines: (variantId: string) =>
    api.get<Array<{ orderId: string; number: number; warehouseId: string; storeId: string | null; supplierName: string | null; pendingQty: number }>>(
      `/purchasing/open-lines?variantId=${encodeURIComponent(variantId)}`,
    ),
  createOrder: (data: SupplierOrderInput) => api.post<SupplierOrderDetail>("/purchasing/orders", data),
  updateOrder: (id: string, data: SupplierOrderInput) => api.put<SupplierOrderDetail>(`/purchasing/orders/${id}`, data),
  markOrdered: (id: string) => api.patch<SupplierOrderDetail>(`/purchasing/orders/${id}/ordered`, {}),
  cancelOrder: (id: string) => api.patch<SupplierOrderDetail>(`/purchasing/orders/${id}/cancel`, {}),
  receive: (
    id: string,
    data: {
      operationId: string;
      allowOverReceive?: boolean;
      reference?: string;
      notes?: string;
      lines: Array<{ lineId: string; quantity: number; unitCost?: number | null; expiryAt?: string | null; identifiers?: string[] }>;
    },
  ) => api.post<SupplierOrderDetail>(`/purchasing/orders/${id}/receive`, data),
};

export const suppliersApi = {
  getAll: () =>
    api.get<
      Array<{
        id: string;
        name: string;
        email?: string;
        phone?: string;
        address?: string;
        _count?: { products: number };
      }>
    >("/suppliers"),

  getOne: (id: string) => api.get(`/suppliers/${id}`),

  create: (data: {
    name: string;
    email?: string;
    phone?: string;
    address?: string;
  }) => api.post<{ id: string; name: string }>("/suppliers", data),

  update: (
    id: string,
    data: { name?: string; email?: string; phone?: string; address?: string },
  ) => api.patch(`/suppliers/${id}`, data),

  delete: (id: string) => api.delete(`/suppliers/${id}`),
};

// POS API
export interface SaleLine {
  id: string;
  quantity: number;
  quantityReturned?: string;
  serializedUnitId?: string | null;
  unitPrice: string;
  lineSubtotal: string;
  lineDiscount: string;
  lineTotal: string;
  discountType?: "PERCENT" | "AMOUNT" | null;
  discountValue?: string | null;
  appliedPromotionId?: string | null;
  appliedPriceSource?: string | null;
  priceType?: string | null;
  lineDisposition?: "SALE" | "GIFT";
  giftReasonCode?: string | null;
  giftReasonText?: string | null;
  note?: string;
  extras?: Array<{ name: string; price: number }>;
  imei1Snapshot?: string | null;
  imei2Snapshot?: string | null;
  serialNumberSnapshot?: string | null;
  appliedPromotion?: { name: string } | null;
  variant: {
    id: string;
    sku: string;
    serializedTracking?: boolean;
    size?: string;
    color?: string;
    product: { name: string; brand?: string };
  };
}

export interface ScanProductSelectionResponse {
  requiresSerializedSelection: true;
  variantId: string;
  variantName?: string | null;
  productName?: string | null;
  sku?: string | null;
  availableCount: number;
  serializedTracking: true;
}

export interface Sale {
  id: string;
  number: number | null; // Nullable - assigned only at checkout
  status: "DRAFT" | "PAID" | "CANCELLED" | "VOIDED";
  origin: "IN_STORE" | "ONLINE";
  onlineChannel?: string;
  contactId?: string | null;
  sourceQuoteId?: string | null;
  subtotal: string;
  discountTotal: string;
  taxTotal: string;
  total: string;
  globalDiscountType?: "PERCENT" | "AMOUNT";
  globalDiscountValue?: string;
  lines: SaleLine[];
  payments?: SalePaymentSummary[];
  contact?: ContactSearchResult | null;
  cashSession?: { id: string; register?: { name: string } | null } | null;
  returnStatus?: "NONE" | "PARTIAL" | "FULL";
  returns?: SaleReturnSummary[];
  voidRecord?: {
    id: string;
    reason?: string | null;
    voidedAt?: string | null;
  } | null;
  createdAt: string;
  paidAt?: string; // Added for Dashboard metrics
  lastActivityAt?: string; // For parked sales expiration
  createdBy?: { id?: string; firstName: string; lastName: string; username?: string | null };
  paidBy?: { id?: string; firstName: string; lastName: string; username?: string | null };
  salespersonId?: string | null;
  salespersonNameSnapshot?: string | null;
  salesperson?: { id: string; name: string; isActive?: boolean } | null;
  // Restaurant fields
  orderType?: "DINE_IN" | "TAKEOUT";
  orderNumber?: number | null;
  kitchenStatus?:
    | "NEW"
    | "IN_PROGRESS"
    | "READY"
    | "COMPLETED"
    | "CANCELLED"
    | null;
  kitchenNote?: string | null;
  tableId?: string | null;
  table?: { name: string } | null;
  // Fiscal invoicing — present (0 or 1 entry in practice) only when this sale
  // issued a Factura. Absent/empty means Comprobante.
  fiscalDocuments?: Array<{ id: string; formattedNumber: string; voidedAt?: string | null }>;
}

export interface SalePaymentSummary {
  id?: string;
  method: string;
  amount: string;
  cashSessionId?: string | null;
  reference?: string | null;
}

export interface SaleReturnSummary {
  id: string;
  totalRefund: string;
  reason?: string | null;
  status?: string;
  createdAt?: string;
  processedBy?: { firstName?: string | null; lastName?: string | null } | null;
  items?: Array<{
    id: string;
    quantity: string;
    refundAmount: string;
    restock: boolean;
    saleLineId?: string;
    saleLine?: {
      id: string;
      variant?: {
        sku?: string;
        product?: { name?: string };
      };
    };
  }>;
  refundBreakdown?: Array<{
    method: string;
    amount: string;
    status: "COMPLETED" | "PENDING";
    label?: string;
  }>;
  cashMovement?: {
    sessionId?: string | null;
    amount?: string | number;
    reason?: string | null;
  } | null;
}

export interface PosVoidReturnApprovalRequest {
  id: string;
  type: "VOID" | "RETURN";
  status: "PENDING" | "APPROVED" | "REJECTED";
  reason: string;
  saleId: string;
  saleNumber?: number | null;
  storeId?: string | null;
  requestPayload?: Record<string, any>;
  requestedById?: string;
  requestedByName?: string;
  requestedByRole?: string;
  requestedAt: string;
  reviewedById?: string | null;
  reviewedByName?: string | null;
  reviewNote?: string | null;
  reviewedAt?: string | null;
  executedAt?: string | null;
  saleVoidId?: string | null;
  saleReturnId?: string | null;
}

export interface PosActionWorkflowResponse<T = unknown> {
  workflowStatus: "EXECUTED" | "PENDING_APPROVAL" | "REJECTED";
  message: string;
  approvalRequest?: PosVoidReturnApprovalRequest | null;
  result?: T;
}

export interface PosVoidReturnApprovalListResponse {
  items: PosVoidReturnApprovalRequest[];
  total: number;
}

export interface SaleHistoryItem {
  id: string;
  number: number | null;
  status: "PAID" | "VOIDED" | "DRAFT" | "CANCELLED";
  origin?: "IN_STORE" | "ONLINE";
  onlineChannel?: string | null;
  total: string;
  subtotal: string;
  discountTotal: string;
  paidAt?: string | null;
  createdAt: string;
  returnStatus?: "NONE" | "PARTIAL" | "FULL";
  storeId?: string | null;
  createdBy?: { id?: string; firstName: string; lastName: string; username?: string | null };
  paidBy?: { id?: string; firstName: string; lastName: string; username?: string | null };
  salespersonId?: string | null;
  salespersonNameSnapshot?: string | null;
  salesperson?: { id: string; name: string; isActive?: boolean } | null;
  contact?: { id: string; name: string; phone?: string | null };
  cashSession?: { id: string; register?: { name: string } | null } | null;
  voidRecord?: {
    id: string;
    reason?: string | null;
    voidedAt?: string | null;
  } | null;
  returns?: Array<{ id: string; totalRefund: string }>;
  payments?: SalePaymentSummary[];
  _count?: { lines: number };
}

export interface SalesHistoryResponse {
  items: SaleHistoryItem[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export type WholesalePricingBehavior =
  | "ALWAYS_WHEN_CUSTOMER_IS_WHOLESALE"
  | "OPTIONAL_TOGGLE"
  | "AUTO_IF_MEETS_THRESHOLD";

export interface WholesaleEnrollmentRule {
  id: string;
  name?: string;
  enabled: boolean;
  minQty?: number;
  minTotal?: number;
  allowOverride?: boolean;
}

export interface PosPreferences {
  kitchenFlow?: "PAY_FIRST" | "PAY_LATER";
  allowNegativeStock?: boolean;
  customerDisplayEnabled?: boolean;
  customerDisplayTimeout?: number;
  cashReminderEnabled?: boolean;
  cashReminderOpenTime?: string;
  cashReminderCloseTime?: string;
  cashReminderLeadMinutes?: number;
  wholesalePricingBehavior?: WholesalePricingBehavior;
  wholesaleAllowLineOverride?: boolean;
  wholesaleEnrollmentRules?: WholesaleEnrollmentRule[];
  receiptCashierAliases?: Record<string, string>;
  // Salesperson settings
  enableSalespersonSelector?: boolean;
  requireSalespersonOnSale?: boolean;
  // Sales summary email
  salesSummaryEmailEnabled?: boolean;
  salesSummaryEmailFrequencyHours?: number;
  businessOpenTime?: string;
  businessCloseTime?: string;
  // Cash control
  cashControlRequired?: boolean;
}

export interface DashboardSaleSummary {
  id: string;
  number: number | null;
  total: number;
  paidAt?: string | null;
  createdAt: string;
  lineCount: number;
}

export interface DashboardWeekSalesResponse {
  start: string;
  end: string;
  sales: DashboardSaleSummary[];
}

export interface DashboardRecentSalesResponse {
  days: number;
  limit: number;
  from: string;
  to: string;
  sales: DashboardSaleSummary[];
}

const DASHBOARD_HISTORY_PAGE_SIZE = 100;
const DASHBOARD_HISTORY_MAX_PAGES = 10;
const DASHBOARD_HISTORY_CACHE_TTL_MS = 60_000;

let dashboardHistoryCacheExpiresAt = 0;
const dashboardHistoryPageCache = new Map<
  number,
  Promise<SalesHistoryResponse>
>();

function isApiNotFoundError(error: unknown): error is ApiError {
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    (error as { status?: unknown }).status === 404
  );
}

function getDashboardSaleDate(
  sale: Pick<SaleHistoryItem, "paidAt" | "createdAt"> | DashboardSaleSummary,
) {
  return new Date(sale.paidAt || sale.createdAt);
}

function resolveDashboardTimeZone(timeZone?: string | null) {
  return resolveBusinessTimeZone(timeZone ?? storage.getUser()?.tenant?.timezone);
}

function getDashboardSaleDateKey(
  sale: Pick<SaleHistoryItem, "paidAt" | "createdAt"> | DashboardSaleSummary,
  timeZone: string,
) {
  return businessDateKey(sale.paidAt || sale.createdAt, timeZone);
}

/**
 * Week (Mon–Sun) as business date keys in the tenant timezone. `weekStart` may
 * be a business date key ("YYYY-MM-DD") or, for legacy callers, an instant.
 */
function getDashboardWeekRange(weekStart: string | undefined, timeZone: string) {
  const anchorKey = isDateKey(weekStart)
    ? weekStart
    : businessDateKey(weekStart || new Date(), timeZone) ||
      businessDateKey(new Date(), timeZone);
  const start = startOfBusinessWeekKey(anchorKey);
  return { start, end: addDaysToDateKey(start, 6) };
}

function normalizeDashboardSale(sale: SaleHistoryItem): DashboardSaleSummary {
  return {
    id: sale.id,
    number: sale.number,
    total: Number(sale.total || 0),
    paidAt: sale.paidAt ?? null,
    createdAt: sale.createdAt,
    lineCount: sale._count?.lines || 0,
  };
}

function getDashboardHistoryPage(page: number) {
  const now = Date.now();
  if (now > dashboardHistoryCacheExpiresAt) {
    dashboardHistoryPageCache.clear();
    dashboardHistoryCacheExpiresAt = now + DASHBOARD_HISTORY_CACHE_TTL_MS;
  }

  const cached = dashboardHistoryPageCache.get(page);
  if (cached) {
    return cached;
  }

  const request = api
    .get<SalesHistoryResponse>(
      `/pos/history?status=PAID&page=${page}&pageSize=${DASHBOARD_HISTORY_PAGE_SIZE}`,
    )
    .catch((error) => {
      dashboardHistoryPageCache.delete(page);
      throw error;
    });

  dashboardHistoryPageCache.set(page, request);
  return request;
}

/** Pages /pos/history (newest first) until sales older than `startKey`. */
async function getDashboardPaidSalesSince(startKey: string, timeZone: string) {
  const items: SaleHistoryItem[] = [];

  for (let page = 1; page <= DASHBOARD_HISTORY_MAX_PAGES; page += 1) {
    const response = await getDashboardHistoryPage(page);
    const pageItems = response.items || [];

    if (pageItems.length === 0) {
      break;
    }

    items.push(...pageItems);

    const oldestSale = pageItems[pageItems.length - 1];

    if (
      pageItems.length < DASHBOARD_HISTORY_PAGE_SIZE ||
      getDashboardSaleDateKey(oldestSale, timeZone) < startKey
    ) {
      break;
    }
  }

  return items;
}

async function getDashboardRecentSalesFallback(options?: {
  days?: number;
  limit?: number;
}, timeZoneOverride?: string | null): Promise<DashboardRecentSalesResponse> {
  const days = options?.days ?? 30;
  const limit = options?.limit ?? 50;
  const timeZone = resolveDashboardTimeZone(timeZoneOverride);
  const to = new Date();
  const toKey = businessDateKey(to, timeZone);
  const fromKey = addDaysToDateKey(toKey, -Math.max(days - 1, 0));

  const items = await getDashboardPaidSalesSince(fromKey, timeZone);
  const sales = items
    .filter((sale) => {
      const saleDate = getDashboardSaleDate(sale);
      return getDashboardSaleDateKey(sale, timeZone) >= fromKey && saleDate <= to;
    })
    .sort(
      (a, b) =>
        getDashboardSaleDate(b).getTime() - getDashboardSaleDate(a).getTime(),
    )
    .slice(0, limit)
    .map(normalizeDashboardSale);

  return {
    days,
    limit,
    // Business date keys (tenant timezone), inclusive.
    from: fromKey,
    to: toKey,
    sales,
  };
}

async function getDashboardWeekSalesFallback(
  weekStart?: string,
  timeZoneOverride?: string | null,
): Promise<DashboardWeekSalesResponse> {
  const timeZone = resolveDashboardTimeZone(timeZoneOverride);
  const range = getDashboardWeekRange(weekStart, timeZone);
  const items = await getDashboardPaidSalesSince(range.start, timeZone);
  const sales = items
    .filter((sale) => {
      const key = getDashboardSaleDateKey(sale, timeZone);
      return key >= range.start && key <= range.end;
    })
    .sort(
      (a, b) =>
        getDashboardSaleDate(a).getTime() - getDashboardSaleDate(b).getTime(),
    )
    .map(normalizeDashboardSale);

  return {
    // Business date keys (tenant timezone), inclusive.
    start: range.start,
    end: range.end,
    sales,
  };
}

// Commercial presentations of a variant (Unidad implícita + Ristra x12, Caja …).
export interface VariantPresentation {
  id: string;
  variantId: string;
  name: string;
  factorToBase: number;
  salePrice: number | null;
  saleEnabled: boolean;
  purchaseEnabled: boolean;
  isBase: boolean;
  barcode?: string | null;
  sortOrder: number;
  active: boolean;
  lastPurchaseCost?: number | null;
}

export const presentationsApi = {
  list: (variantId: string) =>
    api.get<VariantPresentation[]>(`/presentations/variant/${variantId}`),
  create: (data: {
    variantId: string;
    name: string;
    factorToBase: number;
    salePrice?: number;
    saleEnabled?: boolean;
    purchaseEnabled?: boolean;
    barcode?: string;
    sortOrder?: number;
  }) => api.post<VariantPresentation>("/presentations", data),
  update: (
    id: string,
    data: Partial<{
      name: string;
      factorToBase: number;
      salePrice: number;
      saleEnabled: boolean;
      purchaseEnabled: boolean;
      active: boolean;
      barcode: string;
      sortOrder: number;
    }>,
  ) => api.patch<VariantPresentation>(`/presentations/${id}`, data),
  remove: (id: string) => api.delete(`/presentations/${id}`),
};

// Physical inventory count (Fase 5)
export interface InventoryCountLine {
  id: string;
  variantId: string;
  expectedBaseQty: number | string;
  countedBaseQty: number | string | null;
  differenceBaseQty: number | string | null;
  unitCostSnapshot: number | string;
  countedComponents?: unknown;
  note?: string | null;
}
export interface InventoryCountSession {
  id: string;
  warehouseId: string;
  storeId?: string | null;
  status: "DRAFT" | "IN_PROGRESS" | "COMPLETED" | "CANCELLED";
  startedAt: string;
  completedAt?: string | null;
  note?: string | null;
  lines?: InventoryCountLine[];
}
export interface CompleteCountResult {
  session: InventoryCountSession;
  countedLines: number;
  adjustedCount: number;
  totalDifferenceValue: number;
  /** Variants with IMEI/serial in an older session: never adjusted by quantity. */
  skippedSerialized?: string[];
}

export const countsApi = {
  create: (warehouseId: string, note?: string) =>
    api.post<InventoryCountSession>("/inventory/counts", { warehouseId, ...(note ? { note } : {}) }),
  list: () => api.get<InventoryCountSession[]>("/inventory/counts"),
  get: (id: string) => api.get<InventoryCountSession>(`/inventory/counts/${id}`),
  setLine: (
    id: string,
    data: {
      variantId: string;
      countedBaseQty: number;
      countedComponents?: Array<{ presentationId?: string; name: string; factor: number; count: number }>;
      note?: string;
    },
  ) => api.patch<InventoryCountLine>(`/inventory/counts/${id}/lines`, data),
  complete: (id: string) => api.post<CompleteCountResult>(`/inventory/counts/${id}/complete`, {}),
  cancel: (id: string) => api.post(`/inventory/counts/${id}/cancel`, {}),
};

export const posApi = {
  // Get consolidated POS data (products + cash session)
  getBootstrap: (storeId?: string) =>
    api.get<{
      products: Array<{
        id: string;
        variantId: string; // Flattened
        productId: string;
        productName: string;
        sku: string;
        barcode?: string;
        plu?: string;
        price: number;
        imageUrl?: string | null;
        sellUom?: string;
        totalStock: number;
        effectiveStock?: number;
        availableSerializedCount?: number;
        soldSerializedCount?: number;
        reservedSerializedCount?: number;
        serializedTracking?: boolean;
        canSell?: boolean;
        extras?: Array<{ name: string; price: number }>;
        priceTiers?: Array<{ minQty: number; unitPrice: number }>;
      }>;
      cashSession?: {
        id: string;
        register: { name: string };
        openedAt: string;
        carriedOverFromPreviousDay?: boolean;
      };
      /** The store has no unambiguous sales warehouse: the POS must not charge. */
      salesWarehouseIssue?: { code: string; message: string } | null;
      timestamp: string;
    }>(`/pos/bootstrap${storeId ? `?storeId=${storeId}` : ""}`),

  // Get products with stock for POS grid (Legacy)
  getProductsWithStock: (storeId?: string) =>
    api.get<
      Array<{
        id: string;
        name: string;
        brand?: string;
        variants: Array<{
          id: string;
          sku: string;
          name?: string;
          size?: string;
          color?: string;
          price: number;
          sellUom?: string;
          totalStock: number;
          effectiveStock?: number;
          availableSerializedCount?: number;
          soldSerializedCount?: number;
          reservedSerializedCount?: number;
          serializedTracking?: boolean;
          canSell?: boolean;
        }>;
      }>
    >(`/pos/products${storeId ? `?storeId=${storeId}` : ""}`),

  // Create new sale
  createSale: (data?: {
    origin?: "IN_STORE" | "ONLINE";
    onlineChannel?: string;
    cashSessionId?: string;
    storeId?: string;
    // Restaurant fields
    orderType?: "DINE_IN" | "TAKEOUT";
    tableId?: string;
    kitchenNote?: string;
  }) => api.post<Sale>("/pos/sales", data || {}),

  // Get sale details
  getSale: (saleId: string) => api.get<Sale>(`/pos/sales/${saleId}`),

  /** `weekStart`: business date key "YYYY-MM-DD" (tenant timezone). */
  getDashboardWeekSales: async (weekStart?: string, timeZone?: string) => {
    if (!DASHBOARD_LITE_ENDPOINTS_ENABLED) {
      return getDashboardWeekSalesFallback(weekStart, timeZone);
    }

    const params = new URLSearchParams();
    if (weekStart) params.set("weekStart", weekStart);

    try {
      return await api.get<DashboardWeekSalesResponse>(
        `/pos/dashboard/week-sales${params.toString() ? `?${params.toString()}` : ""}`,
      );
    } catch (error) {
      if (isApiNotFoundError(error)) {
        return getDashboardWeekSalesFallback(weekStart, timeZone);
      }

      throw error;
    }
  },

  getDashboardRecentSales: async (options?: {
    days?: number;
    limit?: number;
  }) => {
    if (!DASHBOARD_LITE_ENDPOINTS_ENABLED) {
      return getDashboardRecentSalesFallback(options);
    }

    const params = new URLSearchParams();
    if (options?.days) params.set("days", String(options.days));
    if (options?.limit) params.set("limit", String(options.limit));

    try {
      return await api.get<DashboardRecentSalesResponse>(
        `/pos/dashboard/recent-sales${params.toString() ? `?${params.toString()}` : ""}`,
      );
    } catch (error) {
      if (isApiNotFoundError(error)) {
        return getDashboardRecentSalesFallback(options);
      }

      throw error;
    }
  },

  getHistory: (filters?: {
    search?: string;
    status?: string;
    page?: number;
    pageSize?: number;
    storeId?: string;
    cashSessionId?: string;
    saleId?: string;
    dateFrom?: string;
    dateTo?: string;
    channel?: string;
  }) => {
    const params = new URLSearchParams();
    if (filters?.search) params.set("search", filters.search);
    if (filters?.status) params.set("status", filters.status);
    if (filters?.page) params.set("page", String(filters.page));
    if (filters?.pageSize) params.set("pageSize", String(filters.pageSize));
    if (filters?.storeId) params.set("storeId", filters.storeId);
    if (filters?.cashSessionId)
      params.set("cashSessionId", filters.cashSessionId);
    if (filters?.saleId) params.set("saleId", filters.saleId);
    if (filters?.dateFrom) params.set("dateFrom", filters.dateFrom);
    if (filters?.dateTo) params.set("dateTo", filters.dateTo);
    if (filters?.channel) params.set("channel", filters.channel);
    return api.get<SalesHistoryResponse>(
      `/pos/history${params.toString() ? `?${params.toString()}` : ""}`,
    );
  },

  // Scan product by code
  scanProduct: (saleId: string, code: string, quantity?: number, serializedUnitId?: string, defaultPricingMode?: string) =>
    api.post<Sale | ScanProductSelectionResponse>(`/pos/sales/${saleId}/scan`, {
      code,
      ...(quantity !== undefined && { quantity }),
      ...(serializedUnitId !== undefined && { serializedUnitId }),
      ...(defaultPricingMode && { defaultPricingMode }),
    }),

  // Add line manually
  addLine: (
    saleId: string,
    variantId: string,
    quantity: number,
    note?: string,
    extras?: Array<{ name: string; price: number }>,
    serializedUnitId?: string,
    defaultPricingMode?: string,
    presentationId?: string,
  ) =>
    api.post<Sale>(`/pos/sales/${saleId}/lines`, {
      variantId,
      quantity,
      ...(note && { note }),
      ...(extras?.length && { extras }),
      ...(serializedUnitId ? { serializedUnitId } : {}),
      ...(defaultPricingMode && { defaultPricingMode }),
      ...(presentationId ? { presentationId } : {}),
    }),

  // Update line quantity
  updateLineQty: (
    saleId: string,
    lineId: string,
    quantity: number,
    note?: string,
  ) =>
    api.patch<Sale>(`/pos/sales/${saleId}/lines/${lineId}`, {
      quantity,
      ...(note !== undefined && { note }),
    }),

  // Update line note and/or extras
  updateLineNote: (
    saleId: string,
    lineId: string,
    note: string,
    extras?: Array<{ name: string; price: number }>,
  ) =>
    api.patch<Sale>(`/pos/sales/${saleId}/lines/${lineId}/note`, {
      note,
      ...(extras !== undefined && { extras }),
    }),

  // Remove line
  removeLine: (saleId: string, lineId: string) =>
    api.delete<Sale>(`/pos/sales/${saleId}/lines/${lineId}`),

  // Apply line discount
  applyLineDiscount: (
    saleId: string,
    lineId: string,
    type: "PERCENT" | "AMOUNT",
    value: number,
    reasonCode: string,
    reasonText: string,
  ) =>
    api.patch<Sale>(`/pos/sales/${saleId}/lines/${lineId}/discount`, {
      type,
      value,
      reasonCode,
      reasonText,
    }),

  // Apply global discount
  applyGlobalDiscount: (
    saleId: string,
    type: "PERCENT" | "AMOUNT",
    value: number,
    reasonCode: string,
    reasonText: string,
  ) =>
    api.patch<Sale>(`/pos/sales/${saleId}/discount`, {
      type,
      value,
      reasonCode,
      reasonText,
    }),

  // Mark a line as a commercial gift (regalía) — charges L0, keeps real cost
  applyLineGift: (
    saleId: string,
    lineId: string,
    reasonCode: string,
    reasonText: string,
  ) =>
    api.patch<Sale>(`/pos/sales/${saleId}/lines/${lineId}/gift`, {
      reasonCode,
      reasonText,
    }),

  // Remove gift disposition from a line, restoring its price
  clearLineGift: (saleId: string, lineId: string) =>
    api.delete<Sale>(`/pos/sales/${saleId}/lines/${lineId}/gift`),

  // Cost preview for the gift modal — 403s for roles without cost visibility
  getLineGiftPreview: (saleId: string, lineId: string) =>
    api.get<{ unitCost: string; quantity: number; totalCost: string }>(
      `/pos/sales/${saleId}/lines/${lineId}/gift-preview`,
    ),

  // Set/clear customer on a draft sale (returns full sale with updated prices)
  setCustomer: (saleId: string, contactId: string | null) =>
    api.patch<{ saleId: string; customer: any; sale: Sale }>(
      `/pos/sales/${saleId}/customer`,
      { contactId },
    ),

  // Checkout
  checkout: (
    saleId: string,
    payments: Array<{
      method: string;
      amount: number;
      reference?: string;
      // Card details
      cardProcessorId?: string;
      cardProcessorNameSnapshot?: string;
      authorizationCode?: string;
      last4?: string;
      // Transfer details
      bankAccountId?: string;
      bankAccountNameSnapshot?: string;
      referenceNumber?: string;
      // Cash details
      receivedAmount?: number;
      changeAmount?: number;
      // Financing details
      financingMonths?: number;
    }>,
    contactId?: string | null,
    restaurant?: { orderType?: string; tableId?: string; kitchenNote?: string },
    cashSessionId?: string,
    salesperson?: { id: string; nameSnapshot: string } | null,
    fiscal?: {
      documentType?: "COMPROBANTE" | "FACTURA";
      idempotencyKey?: string;
      invoiceRecipientType?: "FINAL_CONSUMER" | "NAMED_CUSTOMER";
    },
    /** Fresh id per confirm attempt — lets offline sync tell a lost response from a double charge. */
    checkoutAttemptId?: string,
  ) =>
    api.post<{
      sale: Sale;
      totalPaid: number;
      change: number;
      fiscalDocument?: FiscalIssuedDocument | null;
    }>(
      `/pos/sales/${saleId}/checkout`,
      {
        payments,
        ...(contactId ? { contactId } : {}),
        ...(cashSessionId ? { cashSessionId } : {}),
        ...(restaurant?.orderType ? { orderType: restaurant.orderType } : {}),
        ...(restaurant?.tableId ? { tableId: restaurant.tableId } : {}),
        ...(restaurant?.kitchenNote
          ? { kitchenNote: restaurant.kitchenNote }
          : {}),
        ...(salesperson?.id ? { salespersonId: salesperson.id } : {}),
        ...(salesperson?.nameSnapshot
          ? { salespersonNameSnapshot: salesperson.nameSnapshot }
          : {}),
        ...(fiscal?.documentType === "FACTURA"
          ? {
              documentType: "FACTURA",
              idempotencyKey: fiscal.idempotencyKey,
              invoiceRecipientType: fiscal.invoiceRecipientType,
            }
          : {}),
        ...(checkoutAttemptId ? { checkoutAttemptId } : {}),
      },
    ),

  // Offline mode: sync a sale that was completed at the register while
  // disconnected. Only CASH/TRANSFER — card and fiscal issuance never happen
  // through this path (see master-plan §23). Safe to retry: the same
  // offlineClientId always returns the same result, never a duplicate sale.
  // Offline Safety P0: v2 (draft checkout + economic snapshot) or legacy v1.
  // The explicit timeout keeps every attempt well below the queue's
  // STALE_SYNCING_MS (see lib/offline/syncManager.ts).
  syncOfflineSale: (data: OfflineSyncRequest) =>
    api.post<OfflineSyncResponse>("/pos/sales/offline-sync", data, { timeoutMs: 45_000 }),

  repriceSale: (
    saleId: string,
    data: {
      wholesaleMode?: boolean;
      lineModes?: Array<{
        lineId: string;
        mode: "AUTO" | "RETAIL" | "WHOLESALE" | "CASH_TRANSFER" | "CARD_PAYMENT" | "EXTRA_FINANCING" | "RESELLER_WHOLESALE";
        manualUnitPrice?: number;
      }>;
    },
  ) => api.post<Sale>(`/pos/sales/${saleId}/reprice`, data),

  // Cancel draft
  cancelDraft: (saleId: string) =>
    api.post<{ message: string }>(`/pos/sales/${saleId}/cancel`, {}),

  // Send to kitchen (comanda) — assigns order number, sets kitchenStatus=NEW
  sendToKitchen: (
    saleId: string,
    data?: { orderType?: string; tableId?: string; kitchenNote?: string },
  ) =>
    api.post<{ id: string; orderNumber: number; kitchenStatus: string }>(
      `/pos/sales/${saleId}/send-to-kitchen`,
      data || {},
    ),

  // Kitchen ticket PDF
  openKitchenTicket: async (saleId: string) => {
    try {
      const blob = await api.getBlob(`/pos/sales/${saleId}/kitchen-ticket.pdf`);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error("Error opening kitchen ticket:", error);
      throw error;
    }
  },

  // Void paid sale
  voidSale: (saleId: string, reason?: string) =>
    api.post<
      PosActionWorkflowResponse<{ success: boolean; voidRecordId?: string }>
    >(`/pos/sales/${saleId}/void`, { reason }),

  // Return items
  returnItems: (
    saleId: string,
    payload: {
      reason: string;
      items: Array<{ lineId: string; quantity: number; restock: boolean }>;
    },
  ) =>
    api.post<PosActionWorkflowResponse<SaleReturnSummary>>(
      `/pos/sales/${saleId}/return`,
      payload,
    ),

  getVoidReturnRequests: (filters?: {
    status?: string;
    type?: "VOID" | "RETURN";
    saleId?: string;
    mine?: boolean;
    limit?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.set("status", filters.status);
    if (filters?.type) params.set("type", filters.type);
    if (filters?.saleId) params.set("saleId", filters.saleId);
    if (filters?.mine) params.set("mine", "true");
    if (filters?.limit) params.set("limit", String(filters.limit));
    return api.get<PosVoidReturnApprovalListResponse>(
      `/pos/void-return-requests${params.toString() ? `?${params.toString()}` : ""}`,
    );
  },

  approveVoidReturnRequest: (requestId: string, note?: string) =>
    api.post<PosActionWorkflowResponse>(
      `/pos/void-return-requests/${requestId}/approve`,
      note ? { note } : {},
    ),

  rejectVoidReturnRequest: (requestId: string, note?: string) =>
    api.post<PosActionWorkflowResponse>(
      `/pos/void-return-requests/${requestId}/reject`,
      note ? { note } : {},
    ),

  // ========== PARKED SALES (DRAFTS) ==========

  // Get all parked/suspended drafts (non-expired)
  getDrafts: () => api.get<Sale[]>("/pos/drafts"),

  // Park (suspend) a sale for later
  parkSale: (saleId: string) => api.post<Sale>(`/pos/sales/${saleId}/park`, {}),

  // Delete a draft sale
  deleteDraft: (saleId: string) =>
    api.delete<{ deleted: boolean; id: string }>(`/pos/sales/${saleId}`),

  // Get receipt PDF URL
  getReceiptPdfUrl: (saleId: string) =>
    `${API_BASE_URL}/pos/sales/${saleId}/receipt.pdf`,

  // Open receipt PDF with authentication
  openReceiptPdf: async (saleId: string) => {
    try {
      const blob = await api.getBlob(`/pos/sales/${saleId}/receipt.pdf`);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");

      // Cleanup after 1 minute
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error("Error opening receipt:", error);
      throw error;
    }
  },

  // Tenant Preferences (owner-configurable)
  getPreferences: (storeId?: string, tenantOverrideId?: string) =>
    api.get<PosPreferences>(
      `/pos/preferences${storeId ? `?storeId=${encodeURIComponent(storeId)}` : ""}`,
      { headers: buildTenantOverrideHeaders(tenantOverrideId) },
    ),
  updatePreferences: (
    prefs: Record<string, any>,
    storeId?: string,
    tenantOverrideId?: string,
  ) =>
    api.patch<{ updated: Record<string, any>; autoChanges?: string[] }>(
      `/pos/preferences${storeId ? `?storeId=${encodeURIComponent(storeId)}` : ""}`,
      prefs,
      { headers: buildTenantOverrideHeaders(tenantOverrideId) },
    ),

  // Tenant Business Info
  getTenantInfo: () =>
    api.get<{
      name: string;
      phone?: string;
      email?: string;
      address?: string;
      currency?: string;
      timezone?: string;
    }>("/pos/tenant-info"),
  updateTenantInfo: (data: {
    name?: string;
    phone?: string;
    email?: string;
    address?: string;
    currency?: string;
    timezone?: string;
  }) =>
    api.patch<{
      name: string;
      phone?: string;
      email?: string;
      address?: string;
      currency?: string;
      timezone?: string;
    }>("/pos/tenant-info", data),

  // Receipt Settings
  getReceiptSettings: () =>
    api.get<{
      id?: string;
      businessName?: string;
      logoUrl?: string;
      address?: string;
      email?: string;
      phone?: string;
      receiptFooterNote?: string;
      // Optional social & business fields
      rtn?: string;
      website?: string;
      instagram?: string;
      facebook?: string;
      tiktok?: string;
    }>("/pos/settings/receipt"),

  updateReceiptSettings: (data: {
    businessName?: string;
    logoUrl?: string;
    address?: string;
    email?: string;
    phone?: string;
    receiptFooterNote?: string;
    rtn?: string;
    website?: string;
    instagram?: string;
    facebook?: string;
    tiktok?: string;
  }) => api.request("/pos/settings/receipt", { method: "PUT", body: data }),

  // Logo upload
  uploadLogo: async (file: File): Promise<{ logoUrl: string }> => {
    const formData = new FormData();
    formData.append("file", file);
    return api.requestFormData<{ logoUrl: string }>(
      "/pos/settings/receipt/logo",
      formData,
    );
  },

  deleteLogo: () =>
    api.request<{ success: boolean }>("/pos/settings/receipt/logo", {
      method: "DELETE",
    }),
};

// ========== Customers API (for POS customer search) ==========

export interface ContactSearchResult {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  idNumber?: string;
  rtn?: string | null;
  type: string;
  isCreditCustomer: boolean;
  creditLimit?: number;
  currentBalance?: number;
  customerStatus?: string;
}

export const customersApi = {
  search: (params: { search?: string; page?: number; limit?: number } = {}) =>
    api.get<{
      data: ContactSearchResult[];
      total: number;
      page: number;
      limit: number;
    }>(
      `/customers?search=${encodeURIComponent(params.search || "")}&page=${params.page || 1}&limit=${params.limit || 20}`,
    ),
  quickCreate: (data: { name?: string; phone?: string; rtn?: string }) =>
    api.post<ContactSearchResult>("/customers/quick-create", data),
  update: (
    id: string,
    data: {
      name?: string;
      phone?: string;
      email?: string;
      address?: string;
      idNumber?: string;
      isCreditCustomer?: boolean;
      creditLimit?: number;
      customerStatus?: string;
      type?: "RETAIL" | "WHOLESALE";
      typeChangeReason?: string;
      rtn?: string;
    },
  ) => api.patch<Contact>(`/customers/${id}`, data),
};

// ========== SaaS Admin API ==========

// Tenant Interface is defined at the top

export interface TenantUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  username?: string;
  role: string;
  isActive: boolean;
  status: UserStatus;
  mustChangePassword?: boolean;
  createdAt: string;
  latestInvitation?: LatestInvitationSummary | null;
  store?: {
    id: string;
    name: string;
  } | null;
  memberships?: Array<{
    role: { name: string };
  }>;
}

export interface ImpersonateResponse {
  accessToken: string;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  industry: string;
  expiresIn: number;
  scope: string;
}

export interface InvitationDispatchResult {
  deliveryStatus: "failed" | "sent";
  deliveryError?: string | null;
  expiresAt: string;
  invitationId: string;
  lastSentAt?: string | null;
  type: "ACCOUNT_ACTIVATION" | "PASSWORD_RESET";
}

export interface CreateTenantInput {
  name?: string;
  businessName?: string;
  slug: string;
  industry?: TenantIndustry;
  plan?: Tenant["plan"];
  status?: TenantStatus;
  features?: Tenant["features"];
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
  address?: string;
  currency?: string;
  timezone?: string;
  contractStartAt?: string | null;
  trialEndsAt?: string | null;
  adminNote?: string;
  seedSuggestedCategories?: boolean;
  initialCategories?: string[];
  storeName?: string;
  warehouseName?: string;
  ownerFirstName?: string;
  ownerFullName?: string;
  ownerLastName?: string;
  ownerEmail?: string;
  ownerPhone?: string;
  businessEmail?: string;
  displayNameForReceipts?: string;
  logoUrl?: string;
  enableWholesalePricing?: boolean;
  allowNegativeStock?: boolean;
  inventoryBootstrapMode?: InventoryBootstrapMode;
  maxUsers?: number;
}

export interface ChangeTenantStatusInput {
  status: TenantStatus;
  reason?: string;
  note?: string;
}

export interface ChangeTenantFiscalModeInput {
  mode: TenantDocumentMode;
  reason?: string;
}

export const saasApi = {
  // Get Global Metrics
  getMetrics: (days = 30) =>
    api.get<{
      tenants: {
        total: number;
        byStatus: { status: string; count: number }[];
        byIndustry: { industry: string; count: number }[];
      };
      users: { total: number };
      sales: { total: number };
      period: string;
    }>(`/saas/metrics/overview?days=${days}`, {
      headers: { "x-tenant-id": "" },
    }),

  // Get Audit Logs
  getAuditLogs: (
    page = 1,
    limit = 50,
    filters?: {
      tenantId?: string;
      action?: string;
      userId?: string;
      category?: string;
    },
  ) => {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(filters?.tenantId && { tenantId: filters.tenantId }),
      ...(filters?.action && { action: filters.action }),
      ...(filters?.userId && { userId: filters.userId }),
      ...(filters?.category && { category: filters.category }),
    });
    return api.get<{
      data: Array<{
        id: string;
        action: string;
        category?: string;
        resource: string;
        resourceId?: string;
        tenantId?: string;
        userId?: string;
        ipAddress?: string;
        createdAt: string;
        metadata?: any;
      }>;
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>(`/saas/audit?${queryParams}`, { headers: { "x-tenant-id": "" } });
  },

  // Global Settings
  getSettings: () =>
    api.get<any>("/saas/settings", { headers: { "x-tenant-id": "" } }),
  updateSettings: (settings: any) =>
    api.patch<any>("/saas/settings", settings, {
      headers: { "x-tenant-id": "" },
    }),

  // List all tenants
  getTenants: (filters?: {
    search?: string;
    status?: TenantStatus | "ALL";
    industry?: TenantIndustry | "ALL";
  }) => {
    const params = new URLSearchParams();
    if (filters?.search?.trim()) params.set("search", filters.search.trim());
    if (filters?.status && filters.status !== "ALL")
      params.set("status", filters.status);
    if (filters?.industry && filters.industry !== "ALL")
      params.set("industry", filters.industry);
    const query = params.toString();
    return api.get<Tenant[]>(`/saas/tenants${query ? `?${query}` : ""}`, {
      headers: { "x-tenant-id": "" },
    });
  },

  // Get single tenant
  getTenant: (id: string) =>
    api.get<Tenant>(`/saas/tenants/${id}`, {
      headers: buildTenantOverrideHeaders(id),
    }),

  // Get tenant context (features, settings)
  getTenantContext: (id: string) =>
    api.get<Tenant>(`/saas/tenants/${id}/context`, {
      headers: buildTenantOverrideHeaders(id),
    }),

  // Create tenant
  checkSlugAvailable: (slug: string) =>
    api.get<{ available: boolean }>(`/saas/tenants/slug-check?slug=${encodeURIComponent(slug)}`, {
      headers: { "x-tenant-id": "" },
    }),

  createTenant: (data: CreateTenantInput) =>
    api.post<Tenant>("/saas/tenants", data, { headers: { "x-tenant-id": "" } }),

  // Update tenant
  updateTenant: (id: string, data: Partial<Tenant>) =>
    // autoChanges: dependency-engine adjustments (only when something changed).
    api.patch<Tenant & { autoChanges?: string[] }>(`/saas/tenants/${id}`, data, {
      headers: buildTenantOverrideHeaders(id),
    }),

  changeTenantStatus: (id: string, data: ChangeTenantStatusInput) =>
    api.post<Tenant>(`/saas/tenants/${id}/status`, data, {
      headers: buildTenantOverrideHeaders(id),
    }),

  // Fiscal invoicing — SUPER_ADMIN exclusive (never self-service by the
  // tenant's own OWNER): document mode + kill switch.
  changeTenantFiscalMode: (id: string, data: ChangeTenantFiscalModeInput) =>
    api.post<Tenant>(`/saas/tenants/${id}/fiscal-mode`, data, {
      headers: buildTenantOverrideHeaders(id),
    }),

  changeTenantFiscalSuspension: (id: string, data: { suspended: boolean; reason?: string }) =>
    api.post<Tenant>(`/saas/tenants/${id}/fiscal-suspension`, data, {
      headers: buildTenantOverrideHeaders(id),
    }),

  getTenantNotes: (id: string) =>
    api.get<{ notes: TenantAdminNoteRecord[] }>(`/saas/tenants/${id}/notes`, {
      headers: buildTenantOverrideHeaders(id),
    }),

  addTenantNote: (id: string, note: string) =>
    api.post<TenantAdminNoteRecord>(
      `/saas/tenants/${id}/notes`,
      { note },
      { headers: buildTenantOverrideHeaders(id) },
    ),

  // Delete tenant (soft)
  deleteTenant: (id: string) =>
    api.delete<{ message: string }>(`/saas/tenants/${id}`, {
      headers: buildTenantOverrideHeaders(id),
    }),

  // Impersonate tenant (Current implementation seems to use the token exchange without extra headers, but safe to add empty tenant id if we are already impersonating someone else? No, if we are SuperAdmin we have no tenantId. If we are impersonating, we shouldn't be calling this? )
  // Actually, "impersonate" is called by SuperAdmin. SuperAdmin has no tenantId. So this is fine.
  impersonate: (tenantId: string) =>
    api.post<ImpersonateResponse>(
      `/saas/impersonate/${tenantId}`,
      {},
      { headers: { "x-tenant-id": "" } },
    ),

  // End impersonation
  // This IS called while impersonating. So we MUST override the header to avoid sending the impersonated tenant ID to the SaaS endpoint?
  // The backend /saas/end-impersonation logic probably relies on the "originalToken" which is client-side logic in `endImpersonation` helper.
  // But if we call the API, we should avoid sending the tenant ID if the endpoint is protected by SuperAdminGuard which might reject valid tenant tokens if not careful?
  // Actually, to end impersonation, we mostly just restore the token on client.
  // But if there is a backend endpoint, let's look at `endImpersonation` function in `SuperAdmin.tsx`.
  // It calls `restoreOriginalToken()` which is synchronous client-side.
  // `saasApi.endImpersonation` calls `/saas/end-impersonation`.
  endImpersonation: () =>
    api.post<{ accessToken: string; refreshToken: string; message: string }>(
      "/saas/end-impersonation",
      {},
      { headers: { "x-tenant-id": "" } },
    ),

  // Get tenant users
  getTenantUsers: (tenantId: string) =>
    api.get<TenantUser[]>(`/saas/tenants/${tenantId}/users`, {
      headers: buildTenantOverrideHeaders(tenantId),
    }),

  resendOwnerInvitation: (tenantId: string) =>
    api.post<InvitationDispatchResult>(
      `/saas/tenants/${tenantId}/owner-invitation/resend`,
      {},
      {
        headers: buildTenantOverrideHeaders(tenantId),
      },
    ),

  updateOwnerEmail: (tenantId: string, email: string) =>
    api.patch<{ email: string }>(
      `/saas/tenants/${tenantId}/owner-email`,
      { email },
      { headers: buildTenantOverrideHeaders(tenantId) },
    ),

  // Create user in tenant
  createTenantUser: (
    tenantId: string,
    data: {
      email: string;
      firstName: string;
      lastName: string;
      roleName: string;
    },
  ) =>
    api.post<TenantUser>(`/saas/tenants/${tenantId}/users`, data, {
      headers: buildTenantOverrideHeaders(tenantId),
    }),

  // Update tenant user
  updateTenantUser: (
    tenantId: string,
    userId: string,
    data: {
      firstName?: string;
      lastName?: string;
      username?: string;
      roleName?: string;
      isActive?: boolean;
    },
  ) =>
    api.patch<{ message: string }>(
      `/saas/tenants/${tenantId}/users/${userId}`,
      data,
      { headers: buildTenantOverrideHeaders(tenantId) },
    ).catch((error: any) => {
      if (error?.status !== 404 && error?.response?.status !== 404) {
        throw error;
      }

      return api.patch<{ message: string }>(
        `/saas/users/${userId}`,
        {
          ...(data.firstName !== undefined ? { firstName: data.firstName } : {}),
          ...(data.lastName !== undefined ? { lastName: data.lastName } : {}),
          ...(data.username !== undefined ? { username: data.username } : {}),
          ...(data.roleName !== undefined ? { role: data.roleName } : {}),
          ...(data.isActive !== undefined ? { isActive: data.isActive } : {}),
        },
        { headers: buildTenantOverrideHeaders(tenantId) },
      );
    }),

  // Current Tenant Users Management
  getUsers: () =>
    api.get<
      Array<{
        id: string;
        firstName: string;
        lastName: string;
        username?: string;
        email: string;
        role: string;
        permissions: string[];
        isActive: boolean;
        lastActivityAt?: string;
        profile?: {
          phone?: string;
          identityNumber?: string;
          jobTitle?: string;
          emergencyContactName?: string;
          emergencyContactPhone?: string;
          addressLine1?: string;
          addressLine2?: string;
          city?: string;
        };
      }>
    >("/saas/users", { headers: { "x-tenant-id": "" } }),

  updateUser: (
    id: string,
    data: {
      firstName?: string;
      lastName?: string;
      username?: string;
      role?: string;
      permissions?: string[];
      isActive?: boolean;
      // Profile fields
      phone?: string;
      identityNumber?: string;
      jobTitle?: string;
      emergencyContactName?: string;
      emergencyContactPhone?: string;
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
    },
    tenantOverrideId?: string,
  ) =>
    api.patch(`/saas/users/${id}`, data, {
      headers: tenantOverrideId
        ? buildTenantOverrideHeaders(tenantOverrideId)
        : { "x-tenant-id": "" },
    }),

  resetPassword: (id: string, tenantOverrideId?: string) =>
    api.patch<{ tempPassword: string }>(
      `/saas/users/${id}/reset-password`,
      {},
      {
        headers: tenantOverrideId
          ? buildTenantOverrideHeaders(tenantOverrideId)
          : { "x-tenant-id": "" },
      },
    ),
};

// ─── Tenant-scoped User Management (ERP) ───────────────────
export interface TenantUserRecord {
  id: string;
  firstName: string;
  lastName: string;
  username?: string;
  email: string;
  role: string;
  permissions: string[];
  isActive: boolean;
  status: UserStatus;
  lastActivityAt?: string;
  createdAt?: string;
  latestInvitation?: LatestInvitationSummary | null;
  /** Stores (sucursales) the user works in, primary first. */
  storeIds?: string[];
  profile?: {
    phone?: string;
    identityNumber?: string;
    jobTitle?: string;
    emergencyContactName?: string;
    emergencyContactPhone?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
  } | null;
}

export interface SalespersonRecord {
  id: string;
  name: string;
  code?: string | null;
  email?: string | null;
  phone?: string | null;
  isActive: boolean;
  /** Commission as a percent of net sales (e.g. 5 = 5%). Null = not configured. */
  commissionRate?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

export const usersApi = {
  /** Assign the stores a user works in (cashier: exactly one). */
  setStores: (userId: string, storeIds: string[]) =>
    api.put<{ userId: string; storeIds: string[]; primaryStoreId: string; warehouseIds: string[] }>(
      `/users/${userId}/stores`,
      { storeIds },
    ),

  /** List all users in the caller's tenant */
  getUsers: () => api.get<TenantUserRecord[]>("/users"),

  /** Seat usage and availability for the caller's tenant */
  getQuota: () => api.get<TenantUserQuotaSummary>("/users/quota"),

  /** Create a new user in the caller's tenant */
  createUser: (data: {
    email: string;
    firstName: string;
    lastName: string;
    username?: string;
    role?: string;
    storeId?: string;
  }) => api.post<TenantUserRecord>("/users", data),

  /** Update user details / permissions / profile */
  updateUser: (
    id: string,
    data: {
      firstName?: string;
      lastName?: string;
      username?: string;
      role?: string;
      permissions?: string[];
      isActive?: boolean;
      phone?: string;
      identityNumber?: string;
      jobTitle?: string;
      emergencyContactName?: string;
      emergencyContactPhone?: string;
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
    },
  ) => api.patch<TenantUserRecord>(`/users/${id}`, data),

  /** Reset password – returns a temp password */
  resetPassword: (id: string) =>
    api.patch<{ tempPassword: string }>(`/users/${id}/reset-password`, {}),

  resendInvitation: (id: string) =>
    api.post<InvitationDispatchResult>(`/users/${id}/resend-invitation`, {}),

  /** Get warehouse assignments + all tenant warehouses for a user */
  getWarehouses: (userId: string) =>
    api.get<{
      allWarehouses: Array<{ id: string; name: string; storeName?: string }>;
      assignedWarehouseIds: string[];
      activeWarehouseId: string | null;
      userRole: string;
    }>(`/users/${userId}/warehouses`),

  /** Set warehouse assignments for a user */
  setWarehouses: (userId: string, warehouseIds: string[]) =>
    api.put(`/users/${userId}/warehouses`, { warehouseIds }),

  /** Set default active warehouse for a user */
  setActiveWarehouse: (userId: string, warehouseId: string) =>
    api.put(`/users/${userId}/active-warehouse`, { warehouseId }),
};

export const salespeopleApi = {
  getSalespeople: (filters?: { activeOnly?: boolean }) => {
    const params = new URLSearchParams();
    if (filters?.activeOnly) params.set("activeOnly", "true");
    return api.get<SalespersonRecord[]>(
      `/pos/salespeople${params.toString() ? `?${params.toString()}` : ""}`,
    );
  },

  createSalesperson: (data: {
    name: string;
    code?: string;
    email?: string;
    phone?: string;
    isActive?: boolean;
    commissionRate?: number | null;
  }) => api.post<SalespersonRecord>("/pos/salespeople", data),

  updateSalesperson: (
    id: string,
    data: {
      name?: string;
      code?: string | null;
      email?: string | null;
      phone?: string | null;
      isActive?: boolean;
      commissionRate?: number | null;
    },
  ) => api.patch<SalespersonRecord>(`/pos/salespeople/${id}`, data),
};

export interface SalesByPersonRow {
  salespersonId: string | null;
  name: string;
  isActive: boolean | null;
  salesCount: number;
  totalSold: number;
  avgTicket: number;
  commissionRate: number | null;
  commission: number;
}

export interface SalesByPersonReport {
  sellers: SalesByPersonRow[];
  totals: { salesCount: number; totalSold: number; commission: number };
}

/** Audit event type used by both tenant and SaaS audit UIs */
export interface AuditEvent {
  id: string;
  action: string;
  category?: string;
  resource: string;
  resourceId?: string;
  tenantId?: string;
  userId?: string;
  ipAddress?: string;
  createdAt: string;
  metadata?: any;
}

export interface AuditLogResponse {
  data: AuditEvent[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

/** Tenant-scoped audit log API (uses /users/audit-log endpoint) */
export const auditApi = {
  getAuditLog: (
    page = 1,
    limit = 50,
    filters?: {
      category?: string;
      action?: string;
      userId?: string;
      from?: string;
      to?: string;
    },
  ) => {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(filters?.category && { category: filters.category }),
      ...(filters?.action && { action: filters.action }),
      ...(filters?.userId && { userId: filters.userId }),
      ...(filters?.from && { from: filters.from }),
      ...(filters?.to && { to: filters.to }),
    });
    return api.get<AuditLogResponse>(`/users/audit-log?${queryParams}`);
  },
};

// Helper to check if current user is Super Admin
export const isSuperAdmin = (): boolean => {
  const token = api.getToken();
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.isSuperAdmin === true;
  } catch {
    return false;
  }
};

export interface CashRegisterRecord {
  id: string;
  name: string;
  storeId?: string | null;
  fiscalEmissionPointId?: string | null;
  currentSession?: { id: string; userId: string; openedAt: string; startAmount: string | number } | null;
}

export const cashApi = {
  getRegisters: (storeId?: string) =>
    api.get<CashRegisterRecord[]>(
      `/pos/cash/registers${storeId ? `?storeId=${storeId}` : ""}`,
    ),

  updateRegisterFiscalEmissionPoint: (registerId: string, fiscalEmissionPointId: string | null) =>
    api.patch<CashRegisterRecord>(`/pos/cash/registers/${registerId}`, { fiscalEmissionPointId }),

  getStatus: (storeId?: string, registerId?: string) => {
    const params = new URLSearchParams();
    if (storeId) params.set("storeId", storeId);
    if (registerId) params.set("registerId", registerId);
    const qs = params.toString();
    return api.get<{ status: "OPEN" | "CLOSED"; register: any; session?: any }>(
      `/pos/cash/status${qs ? `?${qs}` : ""}`,
    );
  },

  openSession: (startAmount: number, registerId: string, storeId?: string) =>
    api.post("/pos/cash/open", {
      startAmount,
      registerId,
      ...(storeId ? { storeId } : {}),
    }),

  closeSession: (endAmount: number, registerId: string, notes?: string) =>
    api.post("/pos/cash/close", { endAmount, registerId, notes }),

  addMovement: (
    type: string,
    amount: number,
    reason: string,
    registerId: string,
    description?: string,
  ) =>
    api.post("/pos/cash/movement", {
      type,
      amount,
      reason,
      registerId,
      ...(description ? { description } : {}),
    }),

  getHistory: (opts?: {
    storeId?: string;
    registerId?: string;
    status?: string;
    page?: number;
    limit?: number;
  }) =>
    api.get<{ sessions: any[]; total: number; page: number; limit: number }>(
      `/pos/cash/history?${new URLSearchParams(
        Object.entries(opts || {})
          .filter(([, v]) => v != null)
          .map(([k, v]) => [k, String(v)]),
      ).toString()}`,
    ),

  getSessionDetail: (sessionId: string) =>
    api.get<any>(`/pos/cash/sessions/${sessionId}`),

  getSummary: (storeId?: string) =>
    api.get<any>(`/pos/cash/summary${storeId ? `?storeId=${storeId}` : ""}`),

  backfillPayments: (data?: { sessionId?: string; storeId?: string }) =>
    api.post<{ updatedPayments: number }>(
      "/pos/cash/payments/backfill",
      data || {},
    ),

  reconcileSession: (sessionId: string, data?: { storeId?: string }) =>
    api.post<any>(`/pos/cash/sessions/${sessionId}/reconcile`, data || {}),
};

export const discountEventsApi = {
  getEvents: (opts?: {
    from?: string;
    to?: string;
    userId?: string;
    scope?: string;
    reasonCode?: string;
    saleId?: string;
    page?: number;
    limit?: number;
  }) =>
    api.get<{ events: any[]; total: number; page: number; limit: number }>(
      `/pos/discount-events?${new URLSearchParams(
        Object.entries(opts || {})
          .filter(([, v]) => v != null)
          .map(([k, v]) => [k, String(v)]),
      ).toString()}`,
    ),
  backfill: () =>
    api.post<{
      scannedSales: number;
      createdEvents: number;
      skippedExisting: number;
    }>("/pos/discount-events/backfill", {}),
};

// Helper to set impersonated token
export const setImpersonatedToken = (token: string) => {
  const currentToken = api.getToken();
  const currentRefresh = storage.getRefreshToken();
  if (currentToken) storage.setOriginalToken(currentToken);
  if (currentRefresh) storage.setOriginalRefreshToken(currentRefresh);
  // Impersonated sessions have no refresh token — admin must log out to re-auth
  api.setAuth(token, "");
};

// Helper to restore original token
export const restoreOriginalToken = () => {
  const originalToken = storage.getOriginalToken();
  const originalRefresh = storage.getOriginalRefreshToken();
  if (originalToken) {
    // Restore both access and refresh tokens so the admin session can renew normally
    api.setAuth(originalToken, originalRefresh ?? "");
    storage.clearOriginalToken();
    storage.clearOriginalRefreshToken();
    sessionStorage.removeItem(KEY_ORIGINAL_TOKEN);
    sessionStorage.removeItem(KEY_ORIGINAL_REFRESH);
  }
};

export const restaurantApi = {
  // Tables
  getTables: (storeId?: string) =>
    api.get<any[]>(`/restaurant/tables${storeId ? `?storeId=${storeId}` : ""}`),
  createTable: (data: { name: string; seats?: number; storeId?: string }) =>
    api.post<any>("/restaurant/tables", data),
  updateTable: (
    id: string,
    data: { name?: string; seats?: number; isActive?: boolean },
  ) => api.patch<any>(`/restaurant/tables/${id}`, data),
  deleteTable: (id: string) => api.delete<any>(`/restaurant/tables/${id}`),

  // Kitchen
  getKitchenOrders: (storeId?: string) =>
    api.get<any[]>(
      `/restaurant/kitchen/orders${storeId ? `?storeId=${storeId}` : ""}`,
    ),
  updateKitchenStatus: (saleId: string, status: string) =>
    api.patch<any>(`/restaurant/kitchen/orders/${saleId}/status`, { status }),
};

// ─── Warehouse Management API ────────────────────────────
export type WarehouseType = "STORE" | "WAREHOUSE" | "BRANCH" | "CENTRAL";

// ========== Promotions & Categories API ==========

export interface Category {
  id: string;
  name: string;
  slug: string;
  sortOrder: number;
  isActive: boolean;
  /** When true, serialized units in this category require an IMEI (phones). */
  requiresImei: boolean;
  parentId: string | null;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
  parent?: {
    id: string;
    name: string;
    isActive: boolean;
  } | null;
  children?: Category[];
  _count?: {
    products: number;
    categoryDiscounts?: number;
  };
}

export type PromotionActionType =
  | "PERCENT_DISCOUNT"
  | "FIXED_DISCOUNT"
  | "BUY_X_PAY_Y"
  | "FIXED_PRICE";
export type PromotionTargetType =
  | "ALL"
  | "CATEGORY"
  | "VARIANT"
  | "VARIANTS"
  | "GROUP";
export type ContactType = "RETAIL" | "WHOLESALE";

export interface PromotionGroup {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  _count?: { items: number };
  items?: Array<{
    id: string;
    variant: {
      id: string;
      sku: string;
      name: string | null;
      product: { name: string };
    };
  }>;
}

export const categoriesApi = {
  getAll: (filters?: { activeOnly?: boolean }, tenantOverrideId?: string) => {
    const params = new URLSearchParams();
    if (filters?.activeOnly) params.set("activeOnly", "true");
    const suffix = params.toString() ? `?${params.toString()}` : "";
    return api.get<Category[]>(`/categories${suffix}`, {
      headers: buildTenantOverrideHeaders(tenantOverrideId),
    });
  },
  getOne: (id: string) => api.get<Category>(`/categories/${id}`),
  create: (data: {
    name: string;
    slug?: string;
    parentId?: string | null;
    sortOrder?: number;
    requiresImei?: boolean;
  }) => api.post<Category>("/categories", data),
  update: (
    id: string,
    data: {
      name?: string;
      slug?: string;
      parentId?: string | null;
      sortOrder?: number;
      isActive?: boolean;
      requiresImei?: boolean;
    },
  ) => api.patch<Category>(`/categories/${id}`, data),
  remove: (id: string) => api.delete<{ deleted: boolean }>(`/categories/${id}`),
};

export interface Promotion {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  priority: number;
  targetType: PromotionTargetType;
  targetCategoryId: string | null;
  targetVariantId: string | null;
  variantIds?: string[]; // For VARIANTS target type
  groupId?: string | null; // For GROUP target type
  actionType: PromotionActionType;
  actionValue: number;
  buyQty: number | null;
  payQty: number | null;
  customerType: ContactType | null;
  minQuantity: number;
  startsAt: string | null;
  endsAt: string | null;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
  targetCategory?: Category | null;
  targetVariant?: { id: string; sku: string; product: { name: string } } | null;
  group?: PromotionGroup | null;
  variants?: Array<{ id: string; variantId: string }>;
}

export interface VariantSearchResult {
  id: string;
  sku: string;
  name: string | null;
  price: number;
  color?: string | null;
  size?: string | null;
  serializedTracking?: boolean;
  sellUom?: string;
  allowDecimals?: boolean;
  barcode?: string | null;
  product: {
    id: string;
    name: string;
    brand: string | null;
  };
}

export interface QuoteProductSearchResult {
  id: string;
  sku: string;
  barcode?: string | null;
  plu?: string | null;
  name: string | null;
  price: number;
  sellUom?: string;
  product: {
    id: string;
    name: string;
    brand: string | null;
  };
}

export interface QuoteResolvedPrice {
  variantId: string;
  sku: string;
  barcode?: string | null;
  plu?: string | null;
  name: string | null;
  displayName: string;
  quantity: number;
  unitPrice: number;
  source: string;
  sellUom?: string;
  basePrice: number;
  product: {
    id: string;
    name: string;
    brand: string | null;
  };
}

export interface QuoteConversionResult {
  saleId: string;
  id: string;
  number: number | null;
  status: string;
  total: number;
  sale?: Sale;
}

export const promotionsApi = {
  // Promotions CRUD
  getAll: () => api.get<Promotion[]>("/pos/promotions"),
  getOne: (id: string) => api.get<Promotion>(`/pos/promotions/${id}`),
  create: (data: Partial<Promotion>) =>
    api.post<Promotion>("/pos/promotions", data),
  update: (id: string, data: Partial<Promotion>) =>
    api.put<Promotion>(`/pos/promotions/${id}`, data),
  remove: (id: string) =>
    api.delete<{ deleted: boolean }>(`/pos/promotions/${id}`),
  toggle: (id: string) =>
    api.patch<Promotion>(`/pos/promotions/${id}/toggle`, {}),

  // Categories CRUD
  getCategories: (filters?: { activeOnly?: boolean }) =>
    categoriesApi.getAll(filters),
  createCategory: (data: {
    name: string;
    parentId?: string | null;
    slug?: string;
    sortOrder?: number;
  }) => categoriesApi.create(data),
  updateCategory: (
    id: string,
    data: {
      name?: string;
      parentId?: string | null;
      slug?: string;
      sortOrder?: number;
      isActive?: boolean;
    },
  ) => categoriesApi.update(id, data),
  removeCategory: (id: string) => categoriesApi.remove(id),

  // Promotion Groups CRUD
  getAllGroups: () => api.get<PromotionGroup[]>("/pos/promotions/groups/all"),
  getOneGroup: (id: string) =>
    api.get<PromotionGroup>(`/pos/promotions/groups/${id}`),
  createGroup: (data: {
    name: string;
    description?: string;
    variantIds: string[];
  }) => api.post<PromotionGroup>("/pos/promotions/groups", data),
  updateGroup: (
    id: string,
    data: { name?: string; description?: string; variantIds?: string[] },
  ) => api.put<PromotionGroup>(`/pos/promotions/groups/${id}`, data),
  removeGroup: (id: string) =>
    api.delete<{ deleted: boolean }>(`/pos/promotions/groups/${id}`),

  // Variant Search
  searchVariants: (query: string, limit?: number) =>
    api.get<VariantSearchResult[]>(
      `/products/variants/search?q=${encodeURIComponent(query)}&limit=${limit || 50}`,
    ),
};

// ========== Customer Pricing API ==========

export interface CustomerPriceEntry {
  id: string;
  contactId: string;
  variantId: string;
  price: number;
  variant: {
    id: string;
    sku: string;
    name: string | null;
    price: number;
    product: { name: string; brand: string | null };
  };
  createdAt: string;
  updatedAt: string;
}

export const customerPricingApi = {
  getCustomerPrices: (contactId: string) =>
    api.get<CustomerPriceEntry[]>(
      `/pos/wholesale/customer-prices/${contactId}`,
    ),
  setCustomerPrice: (contactId: string, variantId: string, price: number) =>
    api.put<CustomerPriceEntry>(
      `/pos/wholesale/customer-prices/${contactId}/${variantId}`,
      { price },
    ),
  removeCustomerPrice: (contactId: string, variantId: string) =>
    api.delete<{ deleted: boolean }>(
      `/pos/wholesale/customer-prices/${contactId}/${variantId}`,
    ),
};

export interface WarehouseAdmin {
  id: string;
  name: string;
  type: WarehouseType;
  location: string | null;
  isActive: boolean;
  storeId: string | null;
  /** defaultWarehouseId = the store's POS sales warehouse. */
  store: { id: string; name: string; defaultWarehouseId?: string | null } | null;
  createdAt: string;
  updatedAt: string;
  _count: { userWarehouses: number; stocks: number };
}

// ==================== FISCAL INVOICING (CAI + rangos autorizados) ====================

export type TenantDocumentMode = "NON_FISCAL" | "FISCAL_REQUIRED" | "HYBRID_CONTROLLED";
export type FiscalRangeStatus = "PENDING" | "ACTIVE" | "EXHAUSTED" | "EXPIRED" | "CANCELLED";

export interface FiscalStatus {
  documentMode: TenantDocumentMode;
  fiscalSuspended: boolean;
  activeRangesCount: number;
}

export interface FiscalProfile {
  id: string;
  tenantId: string;
  legalName: string;
  tradeName?: string | null;
  rtn: string;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  dataConfirmedAt?: string | null;
  dataConfirmedByUserId?: string | null;
  updatedByUserId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FiscalEstablishment {
  id: string;
  tenantId: string;
  code: string;
  name: string;
  address?: string | null;
  storeId?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FiscalEmissionPoint {
  id: string;
  tenantId: string;
  establishmentId: string;
  code: string;
  name?: string | null;
  isActive: boolean;
  establishment?: { code: string; name: string };
  createdAt: string;
  updatedAt: string;
}

export interface FiscalRangeStatusHistoryEntry {
  id: string;
  previousStatus: FiscalRangeStatus;
  nextStatus: FiscalRangeStatus;
  changedByUserId?: string | null;
  reason?: string | null;
  changedAt: string;
}

export interface FiscalRange {
  id: string;
  tenantId: string;
  emissionPointId: string;
  documentType: "FACTURA";
  cai: string;
  rangeFrom: number;
  rangeTo: number;
  nextNumber: number;
  expirationDate: string;
  status: FiscalRangeStatus;
  firstIssuedAt?: string | null;
  lastIssuedAt?: string | null;
  activatedAt?: string | null;
  activatedByUserId?: string | null;
  exhaustedAt?: string | null;
  expiredMarkedAt?: string | null;
  cancelledAt?: string | null;
  cancelledByUserId?: string | null;
  cancelReason?: string | null;
  createdByUserId?: string | null;
  createdAt: string;
  updatedAt: string;
  emissionPoint?: { code: string; establishment: { code: string; name: string } };
  statusHistory?: FiscalRangeStatusHistoryEntry[];
}

/** Shape of the `fiscalDocument` field attached to a checkout response (pos.service.ts). */
export interface FiscalIssuedDocument {
  id: string;
  formattedNumber: string;
  cai: string;
  issuedAt: string;
  rangeExpirationAtIssue: string;
  sequentialNumber: number;
  idempotentReplay: boolean;
}

/** Full fiscal document + immutable emission snapshot, as returned by GET/print (Fase 4). */
export interface FiscalDocumentFull {
  id: string;
  tenantId: string;
  saleId: string;
  documentType: "FACTURA";
  sequentialNumber: number;
  formattedNumber: string;
  cai: string;
  establishmentCode: string;
  emissionPointCode: string;
  issuerRtn: string;
  rangeExpirationAtIssue: string;
  snapshot: {
    issuer: {
      legalName: string;
      tradeName?: string | null;
      rtn: string;
      address?: string | null;
      phone?: string | null;
      email?: string | null;
    };
    customer: {
      recipientType: "FINAL_CONSUMER" | "NAMED_CUSTOMER";
      customerId: string | null;
      name: string | null;
      rtn: string | null;
      address: string | null;
      phone: string | null;
      email: string | null;
    };
    establishment: { code: string };
    emissionPoint: { code: string };
    lines: Array<{
      productName: string;
      sku: string | null;
      quantity: string | number;
      unitPrice: string | number;
      lineDiscount: string | number;
      lineTotal: string | number;
    }>;
    totals: {
      subtotal: string | number;
      discountTotal: string | number;
      taxTotal: string | number;
      total: string | number;
    };
    payments: Array<{ method: string; amount: number }>;
    saleId: string;
    saleNumber: number | null;
  };
  issuedAt: string;
  issuedByUserId?: string | null;
  printRequestCount: number;
  firstPrintRequestedAt?: string | null;
  lastPrintRequestedAt?: string | null;
  lastPrintRequestedById?: string | null;
  voidedAt?: string | null;
  voidReason?: string | null;
}

export const fiscalDocumentApi = {
  getDocument: (id: string) => api.get<FiscalDocumentFull>(`/fiscal-documents/${id}`),
  printDocument: (id: string) =>
    api.post<FiscalDocumentFull>(`/fiscal-documents/${id}/print`, {}),
  // Caso A (Enmienda 1 §4) — anulación por error de emisión, OWNER only.
  markVoided: (id: string, data: { reason: string; revertSale?: boolean }) =>
    api.post<FiscalDocumentFull>(`/fiscal-documents/${id}/mark-voided`, data),
};

export type FiscalResolutionKind = "SALE_VOID" | "SALE_RETURN";
export type FiscalResolutionScope = "TOTAL" | "PARTIAL";
export type FiscalResolutionStatus = "PENDING" | "EXTERNAL_RESOLUTION_RECORDED";

/** Caso B (Enmienda 1 §5/§6) — resolución documental pendiente sobre una venta facturada. */
export interface FiscalPendingResolution {
  id: string;
  tenantId: string;
  fiscalDocumentId: string;
  saleId: string;
  kind: FiscalResolutionKind;
  scope: FiscalResolutionScope;
  saleVoidId?: string | null;
  saleReturnId?: string | null;
  amount: string;
  reason: string;
  requestedByUserId: string;
  requestedByName?: string | null;
  authorizedByUserId?: string | null;
  authorizedByName?: string | null;
  status: FiscalResolutionStatus;
  externalReference?: string | null;
  accountantNote?: string | null;
  closedAt?: string | null;
  closedByUserId?: string | null;
  closedByName?: string | null;
  createdAt: string;
  fiscalDocument?: { id: string; formattedNumber: string };
  sale?: { id: string; number: number | null };
}

/**
 * React Query key for GET /fiscal/status, scoped by tenant so a tenant switch
 * never reuses another tenant's document mode (the POS offline gating depends
 * on it). Invalidating ["fiscal-status"] still matches every tenant (prefix).
 */
export const fiscalStatusQueryKey = (tenantId: string | null | undefined) =>
  ["fiscal-status", tenantId ?? null] as const;

export const fiscalApi = {
  getStatus: (tenantOverrideId?: string) =>
    api.get<FiscalStatus>("/fiscal/status", { headers: buildTenantOverrideHeaders(tenantOverrideId) }),

  getProfile: () => api.get<FiscalProfile | null>("/fiscal/profile"),
  updateProfile: (data: {
    legalName: string;
    tradeName?: string;
    rtn: string;
    address?: string;
    phone?: string;
    email?: string;
    confirmDataAccuracy?: boolean;
  }) => api.put<FiscalProfile>("/fiscal/profile", data),

  listEstablishments: () => api.get<FiscalEstablishment[]>("/fiscal/establishments"),
  createEstablishment: (data: { code: string; name: string; address?: string; storeId?: string }) =>
    api.post<FiscalEstablishment>("/fiscal/establishments", data),
  updateEstablishment: (
    id: string,
    data: { name?: string; address?: string; storeId?: string; isActive?: boolean },
  ) => api.patch<FiscalEstablishment>(`/fiscal/establishments/${id}`, data),

  listEmissionPoints: (establishmentId?: string) =>
    api.get<FiscalEmissionPoint[]>(
      `/fiscal/emission-points${establishmentId ? `?establishmentId=${encodeURIComponent(establishmentId)}` : ""}`,
    ),
  createEmissionPoint: (data: { establishmentId: string; code: string; name?: string }) =>
    api.post<FiscalEmissionPoint>("/fiscal/emission-points", data),
  updateEmissionPoint: (id: string, data: { name?: string; isActive?: boolean }) =>
    api.patch<FiscalEmissionPoint>(`/fiscal/emission-points/${id}`, data),

  listRanges: (filters?: { status?: FiscalRangeStatus; emissionPointId?: string }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.set("status", filters.status);
    if (filters?.emissionPointId) params.set("emissionPointId", filters.emissionPointId);
    const query = params.toString();
    return api.get<FiscalRange[]>(`/fiscal/ranges${query ? `?${query}` : ""}`);
  },
  getRange: (id: string) => api.get<FiscalRange>(`/fiscal/ranges/${id}`),
  createRange: (data: {
    emissionPointId: string;
    cai: string;
    rangeFrom: number;
    rangeTo: number;
    nextNumber?: number;
    expirationDate: string;
    confirmData?: boolean;
  }) => api.post<FiscalRange>("/fiscal/ranges", data),
  updateRange: (
    id: string,
    data: { cai?: string; rangeFrom?: number; rangeTo?: number; nextNumber?: number; expirationDate?: string },
  ) => api.patch<FiscalRange>(`/fiscal/ranges/${id}`, data),
  activateRange: (id: string) => api.post<FiscalRange>(`/fiscal/ranges/${id}/activate`, {}),
  cancelRange: (id: string, reason: string) =>
    api.post<FiscalRange>(`/fiscal/ranges/${id}/cancel`, { reason }),
  exportRangeRemnantsCsv: (id: string) =>
    api.getBlob(`/fiscal/ranges/${id}/remnants.csv`),

  // Caso B (Enmienda 1 §6) — cola de resoluciones documentales pendientes.
  listPendingResolutions: (status?: FiscalResolutionStatus) =>
    api.get<FiscalPendingResolution[]>(
      `/fiscal/pending-resolutions${status ? `?status=${status}` : ""}`,
    ),
  closePendingResolution: (id: string, data: { externalReference: string; accountantNote?: string }) =>
    api.patch<FiscalPendingResolution>(`/fiscal/pending-resolutions/${id}`, data),
};

// ========== FINANCE (Fase 4 — cuentas por pagar/gastos) ==========

export type ExpenseStatus = "PENDING" | "PARTIAL" | "PAID" | "OVERDUE";

export interface ExpenseCategory {
  id: string;
  tenantId: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Expense {
  id: string;
  tenantId: string;
  storeId?: string | null;
  categoryId?: string | null;
  supplierId?: string | null;
  description: string;
  amount: string;
  paidAmount?: string | null;
  dueDate?: string | null;
  status: ExpenseStatus;
  paymentMethod?: string | null;
  note?: string | null;
  paidAt?: string | null;
  createdByUserId?: string | null;
  createdAt: string;
  updatedAt: string;
  category?: { id: string; name: string } | null;
  supplier?: { id: string; name: string } | null;
  store?: { id: string; name: string } | null;
}

export interface ExpenseListResponse {
  items: Expense[];
  total: number;
  page: number;
  limit: number;
}

export interface FinanceSummary {
  totalPending: number;
  pendingCount: number;
  totalOverdue: number;
  overdueCount: number;
  upcoming: Array<{
    id: string;
    description: string;
    amount: number;
    dueDate: string | null;
    categoryName: string | null;
    supplierName: string | null;
  }>;
}

export const financeApi = {
  getSummary: () => api.get<FinanceSummary>("/finance/summary"),

  listCategories: () => api.get<ExpenseCategory[]>("/finance/categories"),
  createCategory: (data: { name: string }) =>
    api.post<ExpenseCategory>("/finance/categories", data),
  updateCategory: (id: string, data: { name?: string; isActive?: boolean }) =>
    api.patch<ExpenseCategory>(`/finance/categories/${id}`, data),

  listExpenses: (filters?: {
    status?: ExpenseStatus;
    categoryId?: string;
    supplierId?: string;
    storeId?: string;
    page?: number;
    limit?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.set("status", filters.status);
    if (filters?.categoryId) params.set("categoryId", filters.categoryId);
    if (filters?.supplierId) params.set("supplierId", filters.supplierId);
    if (filters?.storeId) params.set("storeId", filters.storeId);
    if (filters?.page) params.set("page", String(filters.page));
    if (filters?.limit) params.set("limit", String(filters.limit));
    const query = params.toString();
    return api.get<ExpenseListResponse>(`/finance/expenses${query ? `?${query}` : ""}`);
  },
  getExpense: (id: string) => api.get<Expense>(`/finance/expenses/${id}`),
  createExpense: (data: {
    description: string;
    amount: number;
    categoryId?: string;
    supplierId?: string;
    storeId?: string;
    dueDate?: string;
    note?: string;
  }) => api.post<Expense>("/finance/expenses", data),
  updateExpense: (
    id: string,
    data: {
      description?: string;
      amount?: number;
      categoryId?: string;
      supplierId?: string;
      storeId?: string;
      dueDate?: string;
      note?: string;
    },
  ) => api.patch<Expense>(`/finance/expenses/${id}`, data),
  markPaid: (id: string, data: { paidAmount: number; paymentMethod?: string; paidAt?: string }) =>
    api.patch<Expense>(`/finance/expenses/${id}/mark-paid`, data),
  removeExpense: (id: string) => api.delete<{ success: boolean }>(`/finance/expenses/${id}`),
};

export interface WarehouseAdminListResponse {
  warehouses: WarehouseAdmin[];
  maxWarehouses: number;
  plan: string;
  total: number;
}

export const warehouseApi = {
  setActive: (warehouseId: string) =>
    api.put("/auth/active-warehouse", { warehouseId }),
  listAdmin: () =>
    api.get<WarehouseAdminListResponse>("/inventory/warehouses/admin"),
  create: (data: {
    name: string;
    type?: WarehouseType;
    location?: string;
    storeId?: string;
  }) => api.post<WarehouseAdmin>("/inventory/warehouses", data),
  update: (
    id: string,
    data: {
      name?: string;
      type?: WarehouseType;
      location?: string;
      storeId?: string;
    },
  ) => api.patch<WarehouseAdmin>(`/inventory/warehouses/${id}`, data),
  deactivate: (id: string) =>
    api.post<{ ok: boolean; fallbackWarehouseId?: string }>(
      `/inventory/warehouses/${id}/deactivate`,
      {},
    ),
  activate: (id: string) =>
    api.post<{ ok: boolean }>(`/inventory/warehouses/${id}/activate`, {}),
  /** The store's POS sells (and charges stock) from this warehouse. OWNER only. */
  setStoreSalesWarehouse: (storeId: string, warehouseId: string) =>
    api.put<{ storeId: string; defaultWarehouseId: string; warehouseName: string }>(
      `/inventory/stores/${storeId}/sales-warehouse`,
      { warehouseId },
    ),
};

// ========== WHOLESALE PRICING ==========

export interface WholesalePrice {
  id: string;
  tenantId: string;
  variantId: string;
  price: string;
  variant?: {
    id: string;
    sku: string;
    price: string;
    product?: { name: string };
  };
}

export interface CategoryDiscount {
  id: string;
  tenantId: string;
  categoryId: string;
  discountPercent: number;
  category?: {
    id: string;
    name: string;
  };
}

export const wholesaleApi = {
  // Wholesale Prices
  getPrices: () => api.get<WholesalePrice[]>("/pos/wholesale/prices"),
  setPrice: (variantId: string, price: number) =>
    api.put<WholesalePrice>(`/pos/wholesale/prices/${variantId}`, { price }),
  removePrice: (variantId: string) =>
    api.delete<{ ok: boolean }>(`/pos/wholesale/prices/${variantId}`),
  bulkSetPrices: (entries: Array<{ variantId: string; price: number }>) =>
    api.post<{ count: number }>("/pos/wholesale/prices/bulk", { entries }),

  // Category Discounts
  getCategoryDiscounts: () =>
    api.get<CategoryDiscount[]>("/pos/wholesale/category-discounts"),
  setCategoryDiscount: (categoryId: string, discountPercent: number) =>
    api.put<CategoryDiscount>(
      `/pos/wholesale/category-discounts/${categoryId}`,
      { discountPercent },
    ),
  removeCategoryDiscount: (categoryId: string) =>
    api.delete<{ ok: boolean }>(
      `/pos/wholesale/category-discounts/${categoryId}`,
    ),

  // ── Integrations ──────────────────────────────────────────────────────────
  getShopifyIntegration: () =>
    api.get<{
      enabled: boolean;
      shopDomain: string | null;
      accessTokenConfigured: boolean;
      webhookSecretConfigured: boolean;
      updatedAt: string | null;
    }>("/integrations/shopify"),

  upsertShopifyIntegration: (data: {
    enabled?: boolean;
    shopDomain?: string;
    accessToken?: string;
    webhookSecret?: string;
  }) => api.put<{
    enabled: boolean;
    shopDomain: string | null;
    accessTokenConfigured: boolean;
    webhookSecretConfigured: boolean;
    updatedAt: string | null;
  }>("/integrations/shopify", data),
};

// ========== DISCOUNT POLICY ==========

export interface DiscountPolicy {
  managerMaxPercent: number;
  cashierMaxPercent: number;
  allowLineDiscount: boolean;
  allowGlobalDiscount: boolean;
}

export const discountPolicyApi = {
  get: () => api.get<DiscountPolicy>("/pos/wholesale/discount-policy"),
  update: (data: Partial<DiscountPolicy>) =>
    api.put<DiscountPolicy>("/pos/wholesale/discount-policy", data),
};

// ========== GIFT / REGALÍA POLICY ==========
// Campos completamente separados de DiscountPolicy — nunca comparten fila
// ni tipo, aunque vivan en la misma pantalla de Configuración (RulesTab).

export interface GiftPolicy {
  managerMaxCost: number | null;
  cashierMaxCost: number;
  allowLineGift: boolean;
}

export const giftPolicyApi = {
  get: () => api.get<GiftPolicy>("/pos/wholesale/gift-policy"),
  update: (data: Partial<GiftPolicy>) =>
    api.put<GiftPolicy>("/pos/wholesale/gift-policy", data),
};

// ========== QUOTES / COTIZACIONES ==========

export type QuoteStatus =
  | "DRAFT"
  | "SENT"
  | "ACCEPTED"
  | "REJECTED"
  | "EXPIRED";

export interface Quote {
  id: string;
  quoteNumber: number;
  status: QuoteStatus;
  wholesaleMode?: boolean;
  contactId?: string;
  storeId?: string;
  notes?: string;
  validUntil?: string;
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  total: number;
  globalDiscountType?: "PERCENT" | "AMOUNT";
  globalDiscountValue?: number;
  createdAt: string;
  updatedAt: string;
  sentAt?: string;
  convertedAt?: string | null;
  convertedSaleId?: string | null;
  contact?: {
    id: string;
    name: string;
    phone?: string;
    email?: string;
  };
  store?: {
    id: string;
    name: string;
  };
  createdBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  convertedSale?: {
    id: string;
    number: number | null;
    status: string;
    total?: number;
  } | null;
  lines?: QuoteLine[];
}

export interface QuoteMetrics {
  totalQuotes: number;
  totalConverted: number;
  notConverted: number;
  conversionRate: number;
  breakdown: Record<QuoteStatus, number>;
}

export interface QuoteLine {
  id: string;
  quoteId: string;
  variantId: string;
  sku: string;
  name: string;
  quantity: number;
  unitPrice: number;
  discountType?: "PERCENT" | "AMOUNT";
  discountValue?: number;
  lineSubtotal: number;
  lineDiscount: number;
  lineTotal: number;
  variant?: {
    id: string;
    sku: string;
    name: string;
    price: number;
    product?: {
      id: string;
      name: string;
    };
  };
}

export interface QuoteFilters {
  status?: QuoteStatus;
  contactId?: string;
  from?: string;
  to?: string;
  storeId?: string;
}

export const quotesApi = {
  // List quotes
  getAll: (filters?: QuoteFilters) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append("status", filters.status);
    if (filters?.contactId) params.append("contactId", filters.contactId);
    if (filters?.from) params.append("from", filters.from);
    if (filters?.to) params.append("to", filters.to);
    if (filters?.storeId) params.append("storeId", filters.storeId);
    return api.get<Quote[]>(`/quotes?${params.toString()}`);
  },

  // Get single quote
  getById: (id: string) => api.get<Quote>(`/quotes/${id}`),

  // Search products for quote editor
  searchProducts: (query: string, limit = 20) =>
    api.get<QuoteProductSearchResult[]>(
      `/quotes/product-search?q=${encodeURIComponent(query)}&limit=${limit}`,
    ),

  // Resolve quote line price using POS pricing rules
  resolvePrice: (data: {
    variantId: string;
    quantity?: number;
    contactId?: string;
    wholesale?: boolean;
    pricingMode?: "AUTO" | "RETAIL" | "WHOLESALE";
  }) => api.post<QuoteResolvedPrice>("/quotes/resolve-price", data),

  // Create quote
  create: (data: {
    contactId?: string;
    storeId?: string;
    notes?: string;
    validUntil?: string;
    wholesaleMode?: boolean;
  }) => api.post<Quote>("/quotes", data),

  // Update quote
  update: (
    id: string,
    data: {
      contactId?: string;
      storeId?: string;
      notes?: string;
      validUntil?: string;
      wholesaleMode?: boolean;
      status?: QuoteStatus;
      globalDiscountType?: "PERCENT" | "AMOUNT";
      globalDiscountValue?: number;
    },
  ) => api.patch<Quote>(`/quotes/${id}`, data),

  // Delete quote
  delete: (id: string) => api.delete<{ ok: boolean }>(`/quotes/${id}`),

  // Add line
  addLine: (
    quoteId: string,
    data: {
      variantId: string;
      quantity: number;
      unitPrice?: number;
      discountType?: "PERCENT" | "AMOUNT";
      discountValue?: number;
    },
  ) => api.post<QuoteLine>(`/quotes/${quoteId}/lines`, data),

  // Update line
  updateLine: (
    quoteId: string,
    lineId: string,
    data: {
      quantity?: number;
      unitPrice?: number;
      discountType?: "PERCENT" | "AMOUNT";
      discountValue?: number;
    },
  ) => api.patch<QuoteLine>(`/quotes/${quoteId}/lines/${lineId}`, data),

  // Remove line
  removeLine: (quoteId: string, lineId: string) =>
    api.delete<{ ok: boolean }>(`/quotes/${quoteId}/lines/${lineId}`),

  // Mark as sent
  send: (quoteId: string) => api.post<Quote>(`/quotes/${quoteId}/send`, {}),

  // Convert to sale
  convertToSale: (quoteId: string) =>
    api.post<QuoteConversionResult>(`/quotes/${quoteId}/convert-to-sale`, {}),

  getMetrics: (filters?: { from?: string; to?: string; storeId?: string }) => {
    const params = new URLSearchParams();
    if (filters?.from) params.append("from", filters.from);
    if (filters?.to) params.append("to", filters.to);
    if (filters?.storeId) params.append("storeId", filters.storeId);
    return api.get<QuoteMetrics>(`/quotes/metrics?${params.toString()}`);
  },

  // Get PDF URL
  getPdfUrl: (quoteId: string) => `/quotes/${quoteId}/pdf`,
};

// ── Payment Config (Card Processors + Bank Accounts) ────────────────────────

export interface CardProcessor {
  id: string;
  tenantId: string;
  name: string;
  isActive: boolean;
  cardCommissionRate?: number | null;
}

export interface FinancingPlanRate {
  id: string;
  tenantId: string;
  cardProcessorId: string;
  months: number;
  commissionRate: number;
}

export interface TenantBankAccount {
  id: string;
  tenantId: string;
  bankName: string;
  displayName: string;
  accountNumberMasked?: string;
  currency: string;
  isActive: boolean;
}

export const paymentConfigApi = {
  // Card Processors
  getCardProcessors: () => api.get<CardProcessor[]>("/payment-config/card-processors"),
  createCardProcessor: (name: string) =>
    api.post<CardProcessor>("/payment-config/card-processors", { name }),
  seedDefaultProcessors: () =>
    api.post<{ count: number }>("/payment-config/card-processors/seed-defaults", {}),
  updateCardProcessor: (
    id: string,
    data: { name?: string; isActive?: boolean; cardCommissionRate?: number | null },
  ) => api.patch<CardProcessor>(`/payment-config/card-processors/${id}`, data),

  // Financing Plans
  getFinancingPlans: (processorId: string) =>
    api.get<FinancingPlanRate[]>(`/payment-config/card-processors/${processorId}/financing-plans`),
  upsertFinancingPlan: (processorId: string, months: number, commissionRate: number) =>
    api.post<FinancingPlanRate>(`/payment-config/card-processors/${processorId}/financing-plans`, {
      months,
      commissionRate,
    }),
  deleteFinancingPlan: (id: string) =>
    api.delete(`/payment-config/card-processors/financing-plans/${id}`),

  // Bank Accounts
  getBankAccounts: () => api.get<TenantBankAccount[]>("/payment-config/bank-accounts"),
  createBankAccount: (data: {
    bankName: string;
    displayName: string;
    accountNumberMasked?: string;
    currency?: string;
  }) => api.post<TenantBankAccount>("/payment-config/bank-accounts", data),
  updateBankAccount: (
    id: string,
    data: {
      bankName?: string;
      displayName?: string;
      accountNumberMasked?: string;
      currency?: string;
      isActive?: boolean;
    },
  ) => api.patch<TenantBankAccount>(`/payment-config/bank-accounts/${id}`, data),
  deleteBankAccount: (id: string) =>
    api.delete(`/payment-config/bank-accounts/${id}`),
};

// ── Serialized Inventory (IMEI) API ────────────────────────────────────────

export type SerializedUnitStatus = "AVAILABLE" | "SOLD" | "RESERVED" | "RETURNED_TO_SUPPLIER";

type SerializedInventoryScope = {
  warehouseId?: string;
  storeId?: string;
};

const buildSerializedInventoryQuery = (
  status?: SerializedUnitStatus,
  scope?: SerializedInventoryScope,
) => {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (scope?.warehouseId) params.set("warehouseId", scope.warehouseId);
  if (scope?.storeId) params.set("storeId", scope.storeId);
  const query = params.toString();
  return query ? `?${query}` : "";
};

export interface SerializedInventoryUnit {
  id: string;
  tenantId: string;
  productId: string;
  variantId: string;
  warehouseId: string;
  storeId?: string | null;
  imei1: string | null;
  imei2?: string | null;
  serialNumber?: string | null;
  status: SerializedUnitStatus;
  receivedAt: string;
  soldAt?: string;
  saleLineId?: string;
  costSnapshot?: number | null;
  returnedAt?: string | null;
  returnRefundAmount?: number | null;
  returnNotes?: string | null;
  warehouse?: {
    id: string;
    name: string;
    storeId?: string | null;
  } | null;
  store?: {
    id: string;
    name: string;
  } | null;
  variant?: {
    id: string;
    name: string;
    sku?: string;
    color?: string;
    size?: string;
    price?: number;
  } | null;
  product?: {
    id: string;
    name: string;
    brand?: string;
    imageUrl?: string;
    supplier?: { id: string; name: string } | null;
  } | null;
  saleLines?: Array<{
    id: string;
    unitPrice: number;
    quantity: number;
    sale: {
      id: string;
      number: number;
      paidAt: string | null;
      status: string;
      createdBy?: { id: string; firstName: string; lastName: string } | null;
      contact?: { id: string; name: string; phone?: string } | null;
    };
  }>;
}

export type CostEventType =
  | "PURCHASE"
  | "PART"
  | "LABOR"
  | "DIAGNOSTIC"
  | "CLEANING"
  | "UNLOCK"
  | "ACCESSORY"
  | "PACKAGING"
  | "ADJUSTMENT";

export interface SerializedUnitCostEvent {
  id: string;
  tenantId: string;
  serializedUnitId: string;
  type: CostEventType;
  amount: number;
  note?: string | null;
  userId: string;
  createdAt: string;
  user?: { firstName: string; lastName: string } | null;
}

export const serializedInventoryApi = {
  bulkCreate: (data: {
    productId: string;
    variantId: string;
    warehouseId: string;
    units: Array<{
      imei1?: string;
      imei2?: string;
      serialNumber?: string;
      costSnapshot?: number;
    }>;
    /** RECEIVING (default, real receiving) | OPENING (initial stock at product creation). */
    intakeType?: "RECEIVING" | "OPENING";
    /** Idempotency key: a retry/replay never creates the units twice. */
    operationId?: string;
  }) =>
    api.post<{
      count: number;
      warnings: string[];
      warehouseId: string;
      storeId?: string | null;
      warehouseName?: string;
      idempotentReplay?: boolean;
    }>(
      "/inventory/serialized/bulk",
      data,
    ),

  getByVariant: (
    variantId: string,
    status?: SerializedUnitStatus,
    scope?: SerializedInventoryScope,
  ) =>
    api.get<SerializedInventoryUnit[]>(
      `/inventory/serialized/by-variant/${variantId}${buildSerializedInventoryQuery(status, scope)}`,
    ),

  getByProduct: (productId: string, scope?: SerializedInventoryScope) =>
    api.get<SerializedInventoryUnit[]>(
      `/inventory/serialized/by-product/${productId}${buildSerializedInventoryQuery(undefined, scope)}`,
    ),

  searchByImei: (imei: string, scope?: SerializedInventoryScope) =>
    api.get<SerializedInventoryUnit | null>(
      `/inventory/serialized/search-imei?imei=${encodeURIComponent(imei)}${buildSerializedInventoryQuery(undefined, scope).replace("?", "&")}`,
    ),

  getAvailableCount: (variantId: string, scope?: SerializedInventoryScope) =>
    api.get<{ variantId: string; count: number }>(
      `/inventory/serialized/available-count/${variantId}${buildSerializedInventoryQuery(undefined, scope)}`,
    ),

  updateImei: (id: string, data: { imei1?: string; imei2?: string; serialNumber?: string }) =>
    api.patch<SerializedInventoryUnit>(`/inventory/serialized/${id}`, data),

  returnToSupplier: (id: string, data: { refundAmount?: number; notes?: string }) =>
    api.post<{ success: boolean; unitId: string; imei1: string }>(
      `/inventory/serialized/${id}/return-to-supplier`,
      data,
    ),

  addCostEvent: (
    id: string,
    data: { type: CostEventType; amount: number; note?: string },
  ) =>
    api.post<{ unit: SerializedInventoryUnit; event: SerializedUnitCostEvent }>(
      `/inventory/serialized/${id}/cost-events`,
      data,
    ),

  getCostEvents: (id: string) =>
    api.get<SerializedUnitCostEvent[]>(`/inventory/serialized/${id}/cost-events`),
};

// ─── Planes y Suscripción ────────────────────────────────────────────────────
// Contrato en src/types/subscription.ts. El backend se construye en paralelo
// contra EXACTAMENTE esta forma; consumir verbatim.
import type {
  SubscriptionMeDto,
  SubscriptionStatusDto,
  SubscriptionAuditEvent,
  AdminSubscriptionListRow,
  AdminSubscriptionDetail,
  UpdatePlanPayload,
  SetupSubscriptionPayload,
  PeriodOverridePayload,
  CreateCreditPayload,
  SuspendPayload,
  CancelPayload,
  CreateDiscountPayload,
  RegisterPaymentPayload,
  RegisterAdvancePaymentPayload,
} from "@/types/subscription";

/** Vista del tenant. */
export const subscriptionApi = {
  /** Detalle comercial completo — SOLO OWNER (403 para MANAGER/CASHIER). */
  getMe: () => api.get<SubscriptionMeDto>("/subscription/me"),
  /** Estado mínimo para overlay/bloqueo — cualquier rol autenticado. */
  getStatus: () => api.get<SubscriptionStatusDto>("/subscription/status"),
};

/**
 * Contacto de soporte/cobros de Maddox (WhatsApp), configurable desde SaaS Admin.
 * Legible por cualquier usuario autenticado del tenant.
 */
export const supportApi = {
  getContact: () => api.get<{ whatsappNumber: string }>("/support-contact"),
};

export interface AdminSuggestion {
  id: string;
  title: string;
  description: string;
  status: string;
  createdAt: string;
  tenantName: string;
  authorName: string;
}

/** SaaS-Admin triage of Centro de Ayuda suggestions (SuperAdmin only). */
export const saasSuggestionsApi = {
  list: (params?: { status?: string; page?: number; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.page) q.set("page", String(params.page));
    if (params?.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    return api.get<{
      data: AdminSuggestion[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>(`/saas/suggestions${qs ? `?${qs}` : ""}`, { headers: { "x-tenant-id": "" } });
  },
  updateStatus: (id: string, status: string) =>
    api.patch<{ id: string; status: string }>(
      `/saas/suggestions/${id}`,
      { status },
      { headers: { "x-tenant-id": "" } },
    ),
};

/**
 * Gestión SaaS de suscripciones — SUPER_ADMIN. Igual que el resto de `saasApi`,
 * fuerza `x-tenant-id: ""` para operar a nivel de plataforma, no de un tenant.
 */
const SAAS_HEADERS = { "x-tenant-id": "" } as const;

export const saasSubscriptionApi = {
  list: () =>
    api.get<AdminSubscriptionListRow[]>("/saas/subscriptions", {
      headers: SAAS_HEADERS,
    }),

  getOne: (tenantId: string) =>
    api.get<AdminSubscriptionDetail>(`/saas/subscriptions/${tenantId}`, {
      headers: SAAS_HEADERS,
    }),

  getAudit: (tenantId: string) =>
    api.get<SubscriptionAuditEvent[]>(
      `/saas/subscriptions/${tenantId}/audit`,
      { headers: SAAS_HEADERS },
    ),

  updatePlan: (tenantId: string, payload: UpdatePlanPayload) =>
    api.put<AdminSubscriptionDetail>(
      `/saas/subscriptions/${tenantId}/plan`,
      payload,
      { headers: SAAS_HEADERS },
    ),

  setup: (tenantId: string, payload: SetupSubscriptionPayload) =>
    api.post<AdminSubscriptionDetail>(
      `/saas/subscriptions/${tenantId}/setup`,
      payload,
      { headers: SAAS_HEADERS },
    ),

  addDiscount: (tenantId: string, payload: CreateDiscountPayload) =>
    api.post<AdminSubscriptionDetail>(
      `/saas/subscriptions/${tenantId}/discount`,
      payload,
      { headers: SAAS_HEADERS },
    ),

  removeDiscount: (tenantId: string, discountId: string) =>
    api.delete<AdminSubscriptionDetail>(
      `/saas/subscriptions/${tenantId}/discount/${discountId}`,
      undefined,
      { headers: SAAS_HEADERS },
    ),

  registerPayment: (tenantId: string, payload: RegisterPaymentPayload) =>
    api.post<AdminSubscriptionDetail>(
      `/saas/subscriptions/${tenantId}/payment`,
      payload,
      { headers: SAAS_HEADERS },
    ),

  registerAdvancePayment: (
    tenantId: string,
    payload: RegisterAdvancePaymentPayload,
  ) =>
    api.post<AdminSubscriptionDetail>(
      `/saas/subscriptions/${tenantId}/payment/advance`,
      payload,
      { headers: SAAS_HEADERS },
    ),

  overridePeriod: (
    tenantId: string,
    periodId: string,
    payload: PeriodOverridePayload,
  ) =>
    api.post<AdminSubscriptionDetail>(
      `/saas/subscriptions/${tenantId}/period/${periodId}/override`,
      payload,
      { headers: SAAS_HEADERS },
    ),

  clearPeriodOverride: (tenantId: string, periodId: string) =>
    api.delete<AdminSubscriptionDetail>(
      `/saas/subscriptions/${tenantId}/period/${periodId}/override`,
      undefined,
      { headers: SAAS_HEADERS },
    ),

  createCredit: (tenantId: string, payload: CreateCreditPayload) =>
    api.post<AdminSubscriptionDetail>(
      `/saas/subscriptions/${tenantId}/credit`,
      payload,
      { headers: SAAS_HEADERS },
    ),

  applyCredit: (tenantId: string, creditId: string, periodId?: string) =>
    api.post<AdminSubscriptionDetail>(
      `/saas/subscriptions/${tenantId}/credit/${creditId}/apply`,
      { periodId },
      { headers: SAAS_HEADERS },
    ),

  suspend: (tenantId: string, payload: SuspendPayload = {}) =>
    api.post<AdminSubscriptionDetail>(
      `/saas/subscriptions/${tenantId}/suspend`,
      payload,
      { headers: SAAS_HEADERS },
    ),

  reactivate: (tenantId: string) =>
    api.post<AdminSubscriptionDetail>(
      `/saas/subscriptions/${tenantId}/reactivate`,
      undefined,
      { headers: SAAS_HEADERS },
    ),

  cancel: (tenantId: string, payload: CancelPayload = {}) =>
    api.post<AdminSubscriptionDetail>(
      `/saas/subscriptions/${tenantId}/cancel`,
      payload,
      { headers: SAAS_HEADERS },
    ),
};

/**
 * Product Education — tenant runtime API (contract §6). Any authenticated role.
 * The server is the source of truth; the client reads `/me` and posts append-only,
 * idempotent events keyed by a client-generated `dedupeKey`.
 */
function newEducationDedupeKey(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to a best-effort key */
  }
  return `edu-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export const productEducationApi = {
  getMe: () => api.get<ProductEducationMeDto>("/product-education/me"),

  /** Post an idempotent education event. A `dedupeKey` is generated if omitted. */
  postEvent: (input: EducationEventInput) =>
    api.post<{ ok: boolean }>("/product-education/events", {
      ...input,
      dedupeKey: input.dedupeKey ?? newEducationDedupeKey(),
    }),

  getReleases: () =>
    api.get<ReleaseHistoryItemDto[]>("/product-education/releases"),

  getRelease: (id: string) =>
    api.get<ReleaseHistoryItemDto>(`/product-education/releases/${id}`),
};

/** Super-Admin Release Center (product releases + education). */
export const saasReleaseApi = {
  list: () => api.get<AdminRelease[]>("/saas/releases"),
  get: (id: string) => api.get<AdminRelease>(`/saas/releases/${id}`),
  create: (data: Partial<AdminRelease>) =>
    api.post<AdminRelease>("/saas/releases", data),
  update: (id: string, data: Partial<AdminRelease>) =>
    api.patch<AdminRelease>(`/saas/releases/${id}`, data),
  addItem: (id: string, data: Partial<AdminEducationItem>) =>
    api.post<AdminEducationItem>(`/saas/releases/${id}/items`, data),
  updateItem: (itemId: string, data: Partial<AdminEducationItem>) =>
    api.patch<AdminEducationItem>(`/saas/releases/items/${itemId}`, data),
  deleteItem: (itemId: string) =>
    api.delete<{ ok: boolean }>(`/saas/releases/items/${itemId}`),
  validate: (id: string) =>
    api.post<ValidationResult>(`/saas/releases/${id}/validate`, {}),
  preview: (id: string, body: { role: string; industry: string; features: string[] }) =>
    api.post<PreviewResult>(`/saas/releases/${id}/preview`, body),
  submit: (id: string) => api.post<AdminRelease>(`/saas/releases/${id}/submit`, {}),
  approve: (id: string) => api.post<AdminRelease>(`/saas/releases/${id}/approve`, {}),
  publish: (id: string) => api.post<AdminRelease>(`/saas/releases/${id}/publish`, {}),
  archive: (id: string) => api.post<AdminRelease>(`/saas/releases/${id}/archive`, {}),
  analytics: (id: string) => api.get<ReleaseAnalytics>(`/saas/releases/${id}/analytics`),
  anchors: () => api.get<EducationAnchorInfo[]>("/saas/education/anchors"),
};

// Initialize auth from storage
try {
  syncAuthStateFromStorage();
} catch (error) {
  console.error("[API] Failed to sync auth from storage:", error);
}

